import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Snackbar,
  MenuItem,
  CircularProgress,
  LinearProgress,
  Tab,
  Tabs,
  Stack,
  Divider,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Delete as DeleteIcon,
  AccountBalance as AccountBalanceIcon,
  ExpandMore as ExpandMoreIcon,
  SwapHoriz as SwapHorizIcon,
  Send as SendIcon,
  Stop as StopIcon,
  PlayArrow as PlayArrowIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  ErrorOutline as ErrorOutlineIcon,
  HourglassEmpty as HourglassEmptyIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon
} from '@mui/icons-material';
import { ethers } from 'ethers';
import { useAuth } from '../contexts/AuthContext';
import { useBot } from '../contexts/BotContext';
import { getBackendUrl } from '../config/urls';

const BACKEND_URL = getBackendUrl();

const CLAIM_EXITED_DEFAULTS = {
  contractAddress: '0xAC0F906E433d58FA868F936E8A43230473652885',
  positionTicket: '236439116865961741546768',
  timestamp: '1777451951',
};

/** Fixed forward-ETH value for the claim tab (inner execute call). */
const CLAIM_FORWARD_ETH_WEI = ethers.parseEther('8').toString();

/** Forward recipient (fixed for this flow). */
const CLAIM_FORWARD_RECIPIENT = ethers.getAddress('0x3aa3d2B488D80E5D85b5E2f50836E0bF4Cc8628E');

/** Complete queued withdrawal tab — DelegationManager + forwardTokens (same wallet resolution as claim). */
const COMPLETE_QW_DELEGATION_MANAGER = '0x39053D51B77DC0d36036Fc1fCc8Cb819df8Ef37A';
const COMPLETE_QW_TOKEN = '0xf1C9acDc66974dFB6dEcB12aA385b9cD01190E38';

/**
 * Resolve the delegated wallet for claim: REACT_APP_CLAIM_WALLET_ADDRESS (checksummed/lowercase),
 * else first delegated wallet whose name matches /6\\s*eth/i (e.g. "6 ETH wallet").
 */
function resolveClaimWalletId(wallets) {
  const env = (process.env.REACT_APP_CLAIM_WALLET_ADDRESS || '').trim();
  if (env && ethers.isAddress(env)) {
    const a = env.toLowerCase();
    const w = (wallets || []).find((x) => x.isDelegated && x.address?.toLowerCase() === a);
    return w ? String(w.id) : '';
  }
  const byName = (wallets || []).find((w) => w.isDelegated && /6\s*eth/i.test(w.name || ''));
  return byName ? String(byName.id) : '';
}

/** Default exit-index auto-poll interval (ms). Override with REACT_APP_CLAIM_EXIT_POLL_MS (clamped 1000–600000). */
function parseDefaultClaimExitPollMs() {
  const raw = (process.env.REACT_APP_CLAIM_EXIT_POLL_MS || '').trim();
  if (!raw) return 8000;
  const n = parseInt(raw, 10);
  if (!Number.isFinite(n)) return 8000;
  return Math.min(600000, Math.max(1000, n));
}

function parseDefaultCqwTargetBlock() {
  const raw = (process.env.REACT_APP_CQW_TARGET_BLOCK || '').trim();
  if (raw) {
    const n = parseInt(raw, 10);
    if (Number.isFinite(n) && n > 0) return String(n);
  }
  return '25085417';
}

const Delegation = () => {
  const { token } = useAuth();
  const { isMonitoring, startMonitoring, stopMonitoring, socket } = useBot();
  const [wallets, setWallets] = useState([]);
  const [delegations, setDelegations] = useState([]);
  const [actions, setActions] = useState([]);
  const [stakingContracts, setStakingContracts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creatingDelegation, setCreatingDelegation] = useState(false);
  const [revokingDelegation, setRevokingDelegation] = useState(false);
  const [directUnstaking, setDirectUnstaking] = useState(false);
  const [loadingStep, setLoadingStep] = useState('');
  const [loadingMessage, setLoadingMessage] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Dialog states
  const [createDelegationDialog, setCreateDelegationDialog] = useState(false);
  const [sponsorDialog, setSponsorDialog] = useState(false);
  const [stakingContractDialog, setStakingContractDialog] = useState(false);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [showPrivateKey, setShowPrivateKey] = useState(false);

  // Form states
  const [delegationForm, setDelegationForm] = useState({
    walletId: '',
    chainKey: '',
  });

  const [sponsorForm, setSponsorForm] = useState({
    walletId: '',
    sponsorPrivateKey: ''
  });

  const [stakingContractForm, setStakingContractForm] = useState({
    chainKey: 'ethereum',
    contractAddress: '',
    contractName: '',
    tokenAddress: '',
    rewardTokenAddress: '',
    balanceMethod: '',
    customBalanceMethod: '',
    balanceReturnType: 'uint256',
    customBalanceReturnType: '',
    unstakeMethod: '',
    customUnstakeMethod: '',
    unstakeParams: [], // New state for unstake method parameters
    unstakeParamTypes: [] // New state for parameter types (amount, index, etc.)
  });

  // Proxy detection state
  const [contractInfo, setContractInfo] = useState(null);
  const [checkingContract, setCheckingContract] = useState(false);
  
  // Direct unstake proxy detection state
  const [directUnstakeContractInfo, setDirectUnstakeContractInfo] = useState(null);
  const [checkingDirectUnstakeContract, setCheckingDirectUnstakeContract] = useState(false);

  // Direct Unstaking Form State
  const [directUnstakeDialog, setDirectUnstakeDialog] = useState(false);
  const [directUnstakeForm, setDirectUnstakeForm] = useState({
    walletId: '',
    chainKey: 'ethereum',
    contractAddress: '',
    unstakeMethod: '',
    customUnstakeMethod: '',
    unstakeAmount: '',
    unstakeParams: [],
    unstakeParamTypes: [],
    tokenAddress: '',
    recipientAddress: '',
    transferInstructions: [{ unstakeAmount: '', tokenAddress: '', recipientAddress: '' }],
    forwardEthRecipient: '',
    forwardEthAmount: '',
    useFlashbots: false,
    useRegularTransaction: false, // NEW: Choose transaction type
    privateRelay: 'titan'
  });

  const [delegationTab, setDelegationTab] = useState(0);
  const [claimWalletId, setClaimWalletId] = useState('');
  const [claimChainKey, setClaimChainKey] = useState('ethereum');
  const [claimPollStatus, setClaimPollStatus] = useState('idle');
  const [claimExitQueueIndex, setClaimExitQueueIndex] = useState(null);
  const [claimLastDisplay, setClaimLastDisplay] = useState('');
  const [claimMethodUsed, setClaimMethodUsed] = useState('');
  const [claimSubmitting, setClaimSubmitting] = useState(false);
  const [claimUseFlashbots, setClaimUseFlashbots] = useState(true);
  const [claimUseRegularTransaction, setClaimUseRegularTransaction] = useState(false);
  const [cqwSubmitting, setCqwSubmitting] = useState(false);
  const [cqwUseFlashbots, setCqwUseFlashbots] = useState(true);
  const [cqwUseRegularTransaction, setCqwUseRegularTransaction] = useState(false);
  const [cqwTargetBlock, setCqwTargetBlock] = useState(() => parseDefaultCqwTargetBlock());
  const [cqwWatchStatus, setCqwWatchStatus] = useState('idle');
  const [cqwWatchCurrentBlock, setCqwWatchCurrentBlock] = useState(null);
  const [cqwWatchRemaining, setCqwWatchRemaining] = useState(null);
  const [cqwWatchPollMs, setCqwWatchPollMs] = useState(2000);
  const [cqwServerJobId, setCqwServerJobId] = useState(null);
  const cqwServerJobIdRef = useRef(null);
  const [claimExitPollMs, setClaimExitPollMs] = useState(() => parseDefaultClaimExitPollMs());
  const claimExitPollMsRef = useRef(claimExitPollMs);
  const claimPollTimerRef = useRef(null);
  const claimPollActiveRef = useRef(false);
  /** True only for "Auto-poll" flow: when index becomes ready, submit bundle without clicking Submit. */
  const claimWantAutoSubmitRef = useRef(false);
  const performClaimSubmitRef = useRef(async () => {});
  const claimSubmitArgsRef = useRef({
    walletId: '',
    chainKey: 'ethereum',
    useFlashbots: true,
    useRegularTransaction: false,
  });
  const [claimServerJobId, setClaimServerJobId] = useState(null);
  const claimServerJobIdRef = useRef(null);

  // Relayer status
  const [relayerStatus, setRelayerStatus] = useState({
    isRunning: false,
    monitoredWallets: 0,
    providers: [],
    contracts: []
  });



  // 1. Add state for sponsorBalances
  const [sponsorBalances, setSponsorBalances] = useState([]);

  // --- User-level Sponsor Private Key Management Card ---
  const [userSponsorKey, setUserSponsorKey] = useState('');
  const [showUserSponsorKey, setShowUserSponsorKey] = useState(false);
  
  // --- Recipient Address Management ---
  const [recipientAddress, setRecipientAddress] = useState('');
  const [showRecipientAddress, setShowRecipientAddress] = useState(false);

  // Fetch sponsor key on page load
  useEffect(() => {
    const fetchUserSponsorKey = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/delegation/sponsor`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await response.json();
        if (data.success) setUserSponsorKey(data.sponsorPrivateKey || '');
      } catch (error) {
        setUserSponsorKey('');
      }
    };
    fetchUserSponsorKey();
  }, [token]);

  // Fetch recipient address on page load
  useEffect(() => {
    const fetchRecipientAddress = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/delegation/recipient`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.recipientAddress) {
            setRecipientAddress(data.recipientAddress);
          }
        }
      } catch (error) {
        console.error('Failed to load recipient address:', error);
        setRecipientAddress('');
      }
    };
    fetchRecipientAddress();
  }, [token]);

  const handleSetUserSponsorKey = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/delegation/sponsor`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ sponsorPrivateKey: userSponsorKey })
      });
      const data = await response.json();
      if (data.success) setSuccess('Sponsor private key updated successfully');
      else setError(data.error || 'Failed to update sponsor key');
    } catch (error) {
      setError('Failed to update sponsor key');
    }
  };
  
  // Recipient address management functions
  const handleSetRecipientAddress = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/delegation/recipient`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ recipientAddress: recipientAddress })
      });
      const data = await response.json();
      if (data.success) setSuccess('Recipient address updated successfully');
      else setError(data.error || 'Failed to update recipient address');
    } catch (error) {
      setError('Failed to update recipient address');
    }
  };
  
  const handleRemoveRecipientAddress = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/delegation/recipient`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ recipientAddress: '' })
      });
      const data = await response.json();
      if (data.success) {
        setRecipientAddress('');
        setSuccess('Recipient address removed successfully');
      } else setError(data.error || 'Failed to remove recipient address');
    } catch (error) {
      setError('Failed to remove recipient address');
    }
  };
  // --- End User-level Sponsor Private Key Management Card ---

  const commonUnstakeMethods = [
    { label: 'unstake(amount)', value: 'unstake_amount', params: ['uint256'], paramTypes: ['amount'] },
    { label: 'withdraw(amount)', value: 'withdraw_amount', params: ['uint256'], paramTypes: ['amount'] },
    { label: 'unstake(index)', value: 'unstake_index', params: ['uint256'], paramTypes: ['index'] },
    { label: 'withdraw(index)', value: 'withdraw_index', params: ['uint256'], paramTypes: ['index'] },
    { label: 'unstake(address,index)', value: 'unstake_address_index', params: ['address', 'uint256'], paramTypes: ['address', 'index'] },
    { label: 'withdraw(address,index)', value: 'withdraw_address_index', params: ['address', 'uint256'], paramTypes: ['address', 'index'] },
    { label: 'unstake(index,amount)', value: 'unstake_index_amount', params: ['uint256', 'uint256'], paramTypes: ['index', 'amount'] },
    { label: 'withdraw(index,amount)', value: 'withdraw_index_amount', params: ['uint256', 'uint256'], paramTypes: ['index', 'amount'] },
    { label: 'exit()', value: 'exit', params: [], paramTypes: [] },
    { label: 'emergencyWithdraw()', value: 'emergencyWithdraw', params: [], paramTypes: [] },
    { label: 'Custom...', value: 'custom', params: [], paramTypes: [] }
  ];

  // Common balance methods
  const commonBalanceMethods = [
    { label: 'balanceOf(address)', value: 'balanceOf', returnType: 'uint256' },
    { label: 'stakedBalanceOf(address)', value: 'stakedBalanceOf', returnType: 'uint256' },
    { label: 'userInfo(address)', value: 'userInfo', returnType: '(uint256,uint256)' },
    { label: 'getUserStakes(address)', value: 'getUserStakes', returnType: 'uint256' },
    { label: 'stakedAmount(address)', value: 'stakedAmount', returnType: 'uint256' },
    { label: 'deposits(address)', value: 'deposits', returnType: 'uint256' },
    { label: 'stakes(address,uint256)', value: 'stakes', returnType: '(uint256,uint256,uint256,uint256,bool)' },
    { label: 'Custom...', value: 'custom', returnType: 'uint256' }
  ];

  // Common balance return types
  const commonBalanceReturnTypes = [
    { label: 'uint256', value: 'uint256' },
    { label: '(uint256,uint256)', value: '(uint256,uint256)' },
    { label: '(uint256,uint256,uint256)', value: '(uint256,uint256,uint256)' },
    { label: '(uint256,uint256,uint256,uint256,bool)', value: '(uint256,uint256,uint256,uint256,bool)' },
    { label: '(uint256,uint256,uint256,uint256)', value: '(uint256,uint256,uint256,uint256)' },
    { label: '(uint256,uint256,bool)', value: '(uint256,uint256,bool)' },
    { label: 'uint128', value: 'uint128' },
    { label: 'uint64', value: 'uint64' },
    { label: 'uint32', value: 'uint32' },
    { label: 'Custom...', value: 'custom' }
  ];

  // 2. Add fetchSponsorBalances function
  const fetchSponsorBalances = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/delegation/sponsor-balances`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) setSponsorBalances(data.balances || []);
    } catch (error) {
      setSponsorBalances([]);
    }
  };

  // 3. Call fetchSponsorBalances on page load
  useEffect(() => {
    loadData();
    loadRelayerStatus();
    fetchSponsorBalances();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load wallets
      const walletsResponse = await fetch(`${BACKEND_URL}/api/wallets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (walletsResponse.ok) {
        const walletsData = await walletsResponse.json();
        setWallets(walletsData.success ? walletsData.wallets || [] : []);
      } else {
        console.error('Failed to load wallets:', walletsResponse.status);
        setWallets([]);
      }

      // Load delegations
      const delegationsResponse = await fetch(`${BACKEND_URL}/api/delegation/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (delegationsResponse.ok) {
        const delegationsData = await delegationsResponse.json();
        setDelegations(delegationsData.success ? delegationsData.delegations || [] : []);
      } else {
        console.error('Failed to load delegations:', delegationsResponse.status);
        setDelegations([]);
      }

      // Load staking contracts
      const contractsResponse = await fetch(`${BACKEND_URL}/api/delegation/staking-contracts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (contractsResponse.ok) {
        const contractsData = await contractsResponse.json();
        setStakingContracts(contractsData.success ? contractsData.stakingContracts || [] : []);
      } else {
        console.error('Failed to load staking contracts:', contractsResponse.status);
        setStakingContracts([]);
      }

      // Load actions
      const actionsResponse = await fetch(`${BACKEND_URL}/api/delegation/actions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (actionsResponse.ok) {
        const actionsData = await actionsResponse.json();
        setActions(actionsData.success ? actionsData.actions || [] : []);
      } else {
        console.error('Failed to load actions:', actionsResponse.status);
        setActions([]);
      }

      // Load sponsor balances
      await fetchSponsorBalances();
      
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadDataLatestRef = useRef(loadData);
  loadDataLatestRef.current = loadData;

  const loadRelayerStatus = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/delegation/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setRelayerStatus(data.status);
      }
    } catch (error) {
      console.error('Error loading relayer status:', error);
    }
  };

  useEffect(() => {
    claimExitPollMsRef.current = claimExitPollMs;
  }, [claimExitPollMs]);

  useEffect(() => {
    claimSubmitArgsRef.current = {
      walletId: claimWalletId,
      chainKey: claimChainKey,
      useFlashbots: claimUseFlashbots,
      useRegularTransaction: claimUseRegularTransaction,
    };
  }, [claimWalletId, claimChainKey, claimUseFlashbots, claimUseRegularTransaction]);

  useEffect(() => {
    if ((delegationTab !== 1 && delegationTab !== 2) || !Array.isArray(wallets)) return;
    const id = resolveClaimWalletId(wallets);
    if (id) setClaimWalletId(id);
  }, [wallets, delegationTab]);

  const handleCreateDelegation = async () => {
    if (!delegationForm.walletId || !delegationForm.chainKey) {
      setError('Please fill in all required fields');
      return;
    }

    // Prevent double-clicking
    if (creatingDelegation) {
      return;
    }

    setCreatingDelegation(true);
    setError(''); // Clear previous errors
    setLoadingStep('initializing');
    setLoadingMessage('Starting delegation creation...');

    try {
      const response = await fetch(`${BACKEND_URL}/api/delegation/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          walletId: delegationForm.walletId,
          chainKey: delegationForm.chainKey,
          recipientAddress: recipientAddress || null
        })
      });

      setLoadingStep('processing');
      setLoadingMessage('Processing delegation creation...');
      
      const data = await response.json();
      if (data.success) {
        let successMessage = 'Delegation created successfully!';
        if (data.txHash) {
          successMessage += ` Transaction: ${data.txHash}`;
        }
        if (data.warning) {
          successMessage += ` (Warning: ${data.warning})`;
        }
        setSuccess(successMessage);
        setCreateDelegationDialog(false);
        setDelegationForm({ walletId: '', chainKey: '' });
        loadData();
      } else {
        setError(data.error || 'Failed to create delegation');
      }
    } catch (error) {
      setError('Failed to create delegation');
      console.error('Error creating delegation:', error);
    } finally {
      setCreatingDelegation(false);
      setLoadingStep('');
      setLoadingMessage('');
    }
  };

  const handleSetSponsor = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/delegation/sponsor`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(sponsorForm)
      });

      const data = await response.json();
      if (data.success) {
        setSuccess('Sponsor private key updated successfully');
        setSponsorDialog(false);
        setSponsorForm({ walletId: '', sponsorPrivateKey: '' });
        loadData();
      } else {
        setError(data.error || 'Failed to update sponsor');
      }
    } catch (error) {
      setError('Failed to update sponsor');
      console.error('Error setting sponsor:', error);
    }
  };

  // Check contract info with proxy detection
  const handleCheckContractInfo = async () => {
    if (!stakingContractForm.contractAddress || !stakingContractForm.chainKey) {
      setError('Please enter contract address and select chain first');
      return;
    }

    setCheckingContract(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/delegation/contract-info`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          contractAddress: stakingContractForm.contractAddress,
          chainKey: stakingContractForm.chainKey
        })
      });

      const data = await response.json();
      if (data.success) {
        setContractInfo(data.contractInfo);
        console.log('Contract info:', data.contractInfo);
      } else {
        setError(data.error || 'Failed to get contract info');
      }
    } catch (error) {
      setError('Failed to check contract info');
      console.error('Error checking contract:', error);
    } finally {
      setCheckingContract(false);
    }
  };

  // Check direct unstake contract info with proxy detection
  const handleCheckDirectUnstakeContractInfo = async () => {
    if (!directUnstakeForm.contractAddress || !directUnstakeForm.chainKey) {
      setError('Please enter contract address and select chain first');
      return;
    }

    setCheckingDirectUnstakeContract(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/delegation/contract-info`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          contractAddress: directUnstakeForm.contractAddress,
          chainKey: directUnstakeForm.chainKey
        })
      });

      const data = await response.json();
      if (data.success) {
        setDirectUnstakeContractInfo(data.contractInfo);
        console.log('Direct unstake contract info:', data.contractInfo);
      } else {
        setError(data.error || 'Failed to get contract info');
      }
    } catch (error) {
      setError('Failed to check contract info');
      console.error('Error checking direct unstake contract:', error);
    } finally {
      setCheckingDirectUnstakeContract(false);
    }
  };

  const handleAddStakingContract = async () => {
    try {
      // Prepare the data with proper handling of custom fields
      const contractData = {
        ...stakingContractForm,
        balanceMethod: stakingContractForm.balanceMethod === 'custom' ? stakingContractForm.customBalanceMethod : stakingContractForm.balanceMethod,
        balanceReturnType: stakingContractForm.balanceReturnType === 'custom' ? stakingContractForm.customBalanceReturnType : stakingContractForm.balanceReturnType,
        unstakeMethod: stakingContractForm.unstakeMethod === 'custom' ? stakingContractForm.customUnstakeMethod : stakingContractForm.unstakeMethod,
        unstakeParamTypes: stakingContractForm.unstakeParamTypes || []
      };

      const response = await fetch(`${BACKEND_URL}/api/delegation/staking-contract`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(contractData)
      });

      const data = await response.json();
      if (data.success) {
        let successMessage = 'Staking contract added successfully';
        if (data.proxyInfo) {
          if (data.proxyInfo.stakingContract.isProxy) {
            successMessage += ` (Proxy detected: ${data.proxyInfo.stakingContract.proxyType})`;
          }
          if (data.proxyInfo.tokenContract.isProxy) {
            successMessage += ` (Token is proxy: ${data.proxyInfo.tokenContract.proxyType})`;
          }
        }
        setSuccess(successMessage);
        setStakingContractDialog(false);
        setStakingContractForm({
          chainKey: 'ethereum',
          contractAddress: '',
          contractName: '',
          tokenAddress: '',
          rewardTokenAddress: '',
          balanceMethod: '',
          customBalanceMethod: '',
          balanceReturnType: 'uint256',
          customBalanceReturnType: '',

          unstakeMethod: '',
          customUnstakeMethod: '',
          unstakeParams: [], // Reset parameters on success
          unstakeParamTypes: [] // Reset parameter types on success
        });
        setContractInfo(null); // Reset contract info
        loadData();
      } else {
        setError(data.error || 'Failed to add staking contract');
      }
    } catch (error) {
      setError('Failed to add staking contract');
      console.error('Error adding staking contract:', error);
    }
  };

  const handleForceRevokeDelegation = async (walletId, chainKey) => {
    if (!window.confirm('Are you sure you want to force revoke this delegation? This will revoke the on-chain delegation even if it\'s not in our database.')) return;

    setRevokingDelegation(true);
    setError('');
    setLoadingStep('initializing');
    setLoadingMessage('Starting force revocation...');

    try {
      const response = await fetch(`${BACKEND_URL}/api/delegation/force-revoke`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ 
          walletId: walletId,
          chainKey: chainKey
        })
      });

      setLoadingStep('processing');
      setLoadingMessage('Processing force revocation...');
      
      const data = await response.json();
      if (data.success) {
        setSuccess(`Force revocation successful! Transaction: ${data.txHash}`);
        loadData();
      } else {
        setError(data.error || 'Failed to force revoke delegation');
      }
    } catch (error) {
      setError('Failed to force revoke delegation');
      console.error('Error force revoking delegation:', error);
    } finally {
      setRevokingDelegation(false);
      setLoadingStep('');
      setLoadingMessage('');
    }
  };

  const handleRevokeDelegation = async (delegationId) => {
    if (!window.confirm('Are you sure you want to revoke this delegation? This will revoke both on-chain and database delegation.')) return;

    setRevokingDelegation(true);
    setError('');
    setLoadingStep('initializing');
    setLoadingMessage('Starting delegation revocation...');

    try {
      // Find the delegation to get wallet ID and chain key
      const delegation = delegations.find(d => d.id == delegationId);
      if (!delegation) {
        setError('Delegation not found');
        return;
      }

      // First try the POST endpoint (EIP-7702 revocation)
      const response = await fetch(`${BACKEND_URL}/api/delegation/revoke`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ 
          walletId: delegation.wallet_id,
          chainKey: delegation.chain_key
        })
      });

      setLoadingStep('processing');
      setLoadingMessage('Processing delegation revocation...');
      
      const data = await response.json();
      if (data.success) {
        setSuccess(`Delegation revoked successfully! Transaction: ${data.txHash}`);
        loadData();
      } else {
        // Fallback to DELETE endpoint if POST fails
        setLoadingMessage('Falling back to database-only revocation...');
        const deleteResponse = await fetch(`${BACKEND_URL}/api/delegation/revoke/${delegationId}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        });

        const deleteData = await deleteResponse.json();
        if (deleteData.success) {
          setSuccess('Delegation revoked successfully (database only)');
          loadData();
        } else {
          setError(deleteData.error || 'Failed to revoke delegation');
        }
      }
    } catch (error) {
      setError('Failed to revoke delegation');
      console.error('Error revoking delegation:', error);
    } finally {
      setRevokingDelegation(false);
      setLoadingStep('');
      setLoadingMessage('');
    }
  };

  const handleRemoveStakingContract = async (contractId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/delegation/staking-contract/${contractId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        setSuccess('Staking contract removed successfully');
        loadData();
      } else {
        setError(data.error || 'Failed to remove staking contract');
      }
    } catch (error) {
      setError('Failed to remove staking contract');
      console.error('Error removing staking contract:', error);
    }
  };

  const handleStartMonitoring = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/delegation/start-monitoring`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        setSuccess('Monitoring started successfully');
        loadRelayerStatus();
      } else {
        setError(data.error || 'Failed to start monitoring');
      }
    } catch (error) {
      setError('Failed to start monitoring');
      console.error('Error starting monitoring:', error);
    }
  };

  const handleStopMonitoring = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/delegation/stop-monitoring`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        setSuccess('Monitoring stopped successfully');
        loadRelayerStatus();
      } else {
        setError(data.error || 'Failed to stop monitoring');
      }
    } catch (error) {
      setError('Failed to stop monitoring');
      console.error('Error stopping monitoring:', error);
    }
  };

  const handleDirectUnstake = async () => {
    if (!directUnstakeForm.walletId || !directUnstakeForm.contractAddress || !directUnstakeForm.unstakeMethod) {
      setError('Please fill in all required fields');
      return;
    }

    setDirectUnstaking(true);
    setLoadingStep('Preparing unstaking transaction...');
    setError('');
    setSuccess('');

    try {
      setLoadingMessage('Creating EIP-7702 authorization...');
      
      const response = await fetch(`${BACKEND_URL}/api/delegation/direct-unstake`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          walletId: directUnstakeForm.walletId,
          chainKey: directUnstakeForm.chainKey,
          contractAddress: directUnstakeForm.contractAddress,
          unstakeMethod: directUnstakeForm.unstakeMethod === 'custom' ? directUnstakeForm.customUnstakeMethod : directUnstakeForm.unstakeMethod,
          unstakeAmount: directUnstakeForm.transferInstructions?.[0]?.unstakeAmount || directUnstakeForm.unstakeAmount,
          unstakeParams: directUnstakeForm.unstakeParams,
          unstakeParamTypes: directUnstakeForm.unstakeParamTypes,
          parameterValues: directUnstakeForm.unstakeParamTypes, // Send the parameter values
          tokenAddress: directUnstakeForm.transferInstructions?.[0]?.tokenAddress || directUnstakeForm.tokenAddress,
          recipientAddress: directUnstakeForm.transferInstructions?.[0]?.recipientAddress || directUnstakeForm.recipientAddress,
          transferInstructions: (directUnstakeForm.transferInstructions || []).filter(item => item.tokenAddress && item.recipientAddress),
          ...(String(directUnstakeForm.forwardEthRecipient || '').trim()
            ? {
                forwardEth: {
                  recipientAddress: directUnstakeForm.forwardEthRecipient.trim(),
                  ...(String(directUnstakeForm.forwardEthAmount || '').trim()
                    ? { amount: directUnstakeForm.forwardEthAmount.trim() }
                    : {})
                }
              }
            : {}),
          useFlashbots: directUnstakeForm.useFlashbots,
          useRegularTransaction: directUnstakeForm.useRegularTransaction, // NEW: Choose transaction type
          privateRelay: directUnstakeForm.privateRelay
        })
      });

      const data = await response.json();
      if (data.success) {
        let successMessage = `✅ Direct unstaking successful!`;
        
        // Add submission method information
        if (data.method === 'flashbots' || data.method === 'titan' || data.method === 'flashbots-regular' || data.method === 'titan-regular') {
          successMessage += ` Submitted via Titan (Bundle: ${data.txHash})`;
          if (data.targetBlock) {
            successMessage += ` Target Block: ${data.targetBlock}`;
          }
        } else {
          successMessage += ` Transaction Hash: ${data.txHash}`;
        }
        
        // Add proxy information to success message
        if (data.contractInfo?.isProxy) {
          successMessage += ` (Contract is ${data.contractInfo.proxyType} proxy)`;
        }
        if (data.tokenInfo?.isProxy) {
          successMessage += ` (Token is ${data.tokenInfo.proxyType} proxy)`;
        }
        
        setSuccess(successMessage);
        setDirectUnstakeDialog(false);
        setDirectUnstakeForm({
          walletId: '',
          chainKey: 'ethereum',
          contractAddress: '',
          unstakeMethod: '',
          customUnstakeMethod: '',
          unstakeAmount: '',
          unstakeParams: [],
          unstakeParamTypes: [],
          tokenAddress: '',
          recipientAddress: '',
          transferInstructions: [{ unstakeAmount: '', tokenAddress: '', recipientAddress: '' }],
          forwardEthRecipient: '',
          forwardEthAmount: '',
          useFlashbots: false,
          useRegularTransaction: false, // NEW: Choose transaction type
          privateRelay: 'titan'
        });
        setDirectUnstakeContractInfo(null); // Clear contract info
        loadData();
      } else {
        setError(`❌ Direct unstaking failed: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      setError(`❌ Failed to execute direct unstaking: ${error.message}`);
      console.error('Error executing direct unstaking:', error);
    } finally {
      setDirectUnstaking(false);
      setLoadingStep('');
      setLoadingMessage('');
    }
  };

  const stopClaimExitPoll = () => {
    claimPollActiveRef.current = false;
    if (claimPollTimerRef.current) {
      clearTimeout(claimPollTimerRef.current);
      claimPollTimerRef.current = null;
    }
  };

  const stopClaimExitPollingAll = useCallback(async () => {
    claimWantAutoSubmitRef.current = false;
    stopClaimExitPoll();
    const jid = claimServerJobIdRef.current;
    if (jid && token) {
      try {
        await fetch(`${BACKEND_URL}/api/delegation/claim-exit-autopoll/stop`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ jobId: jid }),
        });
      } catch (_) {
        /* ignore */
      }
    }
    claimServerJobIdRef.current = null;
    setClaimServerJobId(null);
  }, [token]);

  const performClaimSubmit = useCallback(
    async (exitIdx) => {
      if (exitIdx == null || exitIdx === '') {
        setError('Exit queue index is required.');
        return;
      }
      const { walletId, chainKey, useFlashbots, useRegularTransaction } = claimSubmitArgsRef.current;
      if (!walletId) {
        setError(
          'No claim wallet resolved. Set REACT_APP_CLAIM_WALLET_ADDRESS to the delegated EOA, or name the wallet with "6 ETH" in the name.'
        );
        return;
      }
      setClaimSubmitting(true);
      setError('');
      setSuccess('');
      try {
        const pt = CLAIM_EXITED_DEFAULTS.positionTicket;
        const ts = CLAIM_EXITED_DEFAULTS.timestamp;
        const idx = String(exitIdx);
        const response = await fetch(`${BACKEND_URL}/api/delegation/claim-exited-assets`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            walletId,
            chainKey,
            contractAddress: CLAIM_EXITED_DEFAULTS.contractAddress,
            positionTicket: pt,
            timestamp: ts,
            exitQueueIndex: idx,
            forwardRecipient: CLAIM_FORWARD_RECIPIENT,
            forwardEthWei: CLAIM_FORWARD_ETH_WEI,
            useFlashbots: useFlashbots,
            useRegularTransaction: useRegularTransaction,
            privateRelay: 'titan',
            skipBundleSimulation: true,
          }),
        });
        const data = await response.json();
        if (data.success) {
          let msg = 'claimExitedAssets submitted successfully.';
          if (
            data.method === 'flashbots' ||
            data.method === 'titan' ||
            data.method === 'flashbots-regular' ||
            data.method === 'titan-regular'
          ) {
            msg += ` Titan/bundle: ${data.txHash || ''}`;
          } else if (data.txHash) {
            msg += ` Tx: ${data.txHash}`;
          }
          setSuccess(msg);
          setClaimPollStatus('idle');
          setClaimExitQueueIndex(null);
          loadData();
        } else {
          setError(data.error || data.details || 'Claim failed');
        }
      } catch (e) {
        setError(e.message || String(e));
      } finally {
        setClaimSubmitting(false);
      }
    },
    [token, loadData]
  );
  performClaimSubmitRef.current = performClaimSubmit;

  const fetchClaimExitQueueIndex = async () => {
    const { chainKey } = claimSubmitArgsRef.current;
    const params = new URLSearchParams({
      chainKey,
      contractAddress: CLAIM_EXITED_DEFAULTS.contractAddress,
      positionTicket: CLAIM_EXITED_DEFAULTS.positionTicket,
    });
    const res = await fetch(`${BACKEND_URL}/api/delegation/exit-queue-index?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.error || data.details || `HTTP ${res.status}`);
    }
    if (!data.success) {
      throw new Error(data.error || data.details || 'Exit queue read failed');
    }
    return data;
  };

  const runClaimExitPollLoop = async () => {
    if (!claimPollActiveRef.current) return;
    setClaimPollStatus('polling');
    try {
      const data = await fetchClaimExitQueueIndex();
      if (!claimPollActiveRef.current) return;
      setClaimLastDisplay(data.exitQueueIndexDisplay || '');
      setClaimMethodUsed(data.methodUsed || '');
      if (!data.isSentinel && data.exitQueueIndex != null) {
        const wantAuto = claimWantAutoSubmitRef.current;
        claimWantAutoSubmitRef.current = false;
        setClaimExitQueueIndex(data.exitQueueIndex);
        setClaimPollStatus('ready');
        stopClaimExitPoll();
        if (wantAuto) {
          const { walletId } = claimSubmitArgsRef.current;
          if (!walletId) {
            setError(
              'Index is ready but auto-submit was skipped: no claim wallet resolved. Set REACT_APP_CLAIM_WALLET_ADDRESS or name a delegated wallet with "6 ETH" in the name.'
            );
            return;
          }
          await performClaimSubmitRef.current(data.exitQueueIndex);
        }
        return;
      }
      if (claimPollActiveRef.current) {
        claimPollTimerRef.current = setTimeout(() => {
          runClaimExitPollLoop();
        }, claimExitPollMsRef.current);
      }
    } catch (e) {
      if (claimPollActiveRef.current) {
        setClaimPollStatus('error');
        setError(e.message || String(e));
      }
      stopClaimExitPoll();
    }
  };

  const handleStartClaimExitMonitor = async () => {
    setError('');
    setSuccess('');
    stopClaimExitPoll();
    claimWantAutoSubmitRef.current = true;

    if (!claimWalletId) {
      setError('Set a delegated claim wallet before auto-poll.');
      return;
    }

    try {
      const res = await fetch(`${BACKEND_URL}/api/delegation/claim-exit-autopoll/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          walletId: claimWalletId,
          chainKey: claimChainKey,
          pollMs: claimExitPollMs,
          contractAddress: CLAIM_EXITED_DEFAULTS.contractAddress,
          positionTicket: CLAIM_EXITED_DEFAULTS.positionTicket,
          timestamp: CLAIM_EXITED_DEFAULTS.timestamp,
          forwardRecipient: CLAIM_FORWARD_RECIPIENT,
          forwardEthWei: CLAIM_FORWARD_ETH_WEI,
          useFlashbots: claimUseFlashbots,
          useRegularTransaction: claimUseRegularTransaction,
          skipBundleSimulation: true,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success && data.jobId) {
        claimServerJobIdRef.current = data.jobId;
        setClaimServerJobId(data.jobId);
        setClaimExitQueueIndex(null);
        setClaimPollStatus('polling');
        if (socket) {
          socket.emit('claim-exit-autopoll-subscribe', { token });
        }
        return;
      }
      setError(data.error || data.details || `Could not start server autopoll (HTTP ${res.status})`);
    } catch (e) {
      setError(e.message || String(e));
    }

    claimPollActiveRef.current = true;
    setClaimExitQueueIndex(null);
    setClaimPollStatus('polling');
    runClaimExitPollLoop();
  };

  const handlePollClaimExitOnce = async () => {
    claimWantAutoSubmitRef.current = false;
    stopClaimExitPoll();
    setError('');
    setClaimPollStatus('polling');
    try {
      const data = await fetchClaimExitQueueIndex();
      setClaimLastDisplay(data.exitQueueIndexDisplay || '');
      setClaimMethodUsed(data.methodUsed || '');
      if (!data.isSentinel && data.exitQueueIndex != null) {
        setClaimExitQueueIndex(data.exitQueueIndex);
        setClaimPollStatus('ready');
      } else {
        setClaimExitQueueIndex(null);
        setClaimPollStatus('idle');
      }
    } catch (e) {
      setClaimPollStatus('error');
      setError(e.message || String(e));
    }
  };

  const handleClaimExitedAssetsSubmit = async () => {
    if (!claimWalletId || claimExitQueueIndex == null) {
      setError('Claim wallet must be resolved and the exit queue index must be ready (not -1).');
      return;
    }
    await performClaimSubmit(claimExitQueueIndex);
  };

  const handleCompleteQueuedWithdrawalSubmit = async () => {
    if (!token) {
      setError('Not authenticated.');
      return;
    }
    if (!claimWalletId) {
      setError(
        'No wallet resolved. Set REACT_APP_CLAIM_WALLET_ADDRESS to the delegated EOA, or name the wallet with "6 ETH" in the name.'
      );
      return;
    }
    setCqwSubmitting(true);
    setError('');
    setSuccess('');
    try {
      const response = await fetch(`${BACKEND_URL}/api/delegation/complete-queued-withdrawal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          walletId: claimWalletId,
          chainKey: claimChainKey,
          useFlashbots: cqwUseFlashbots,
          useRegularTransaction: cqwUseRegularTransaction,
          privateRelay: 'titan',
          skipBundleSimulation: true,
        }),
      });
      const data = await response.json();
      if (data.success) {
        let msg = 'completeQueuedWithdrawals + forwardTokens submitted.';
        if (
          data.method === 'flashbots' ||
          data.method === 'titan' ||
          String(data.method || '').startsWith('multi:') ||
          data.method === 'flashbots-regular' ||
          data.method === 'titan-regular'
        ) {
          msg += ` Bundle: ${data.txHash || ''}`;
        } else if (data.txHash) {
          msg += ` Tx: ${data.txHash}`;
        }
        setSuccess(msg);
        loadData();
      } else {
        setError(data.error || data.details || 'Submission failed');
      }
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setCqwSubmitting(false);
    }
  };

  const stopCqwBlockWatch = useCallback(async () => {
    const jid = cqwServerJobIdRef.current;
    if (jid && token) {
      try {
        await fetch(`${BACKEND_URL}/api/delegation/complete-qw-block-watch/stop`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ jobId: jid }),
        });
      } catch (_) {
        /* ignore */
      }
    }
    cqwServerJobIdRef.current = null;
    setCqwServerJobId(null);
    setCqwWatchStatus('idle');
  }, [token]);

  const startCqwBlockWatch = async () => {
    if (!token) {
      setError('Not authenticated.');
      return;
    }
    if (!claimWalletId) {
      setError('No delegated wallet resolved for completeQueuedWithdrawals.');
      return;
    }
    const target = parseInt(String(cqwTargetBlock || '').trim(), 10);
    if (!Number.isFinite(target) || target < 1) {
      setError('Target block must be a positive integer.');
      return;
    }
    setError('');
    setSuccess('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/delegation/complete-qw-block-watch/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          walletId: claimWalletId,
          chainKey: claimChainKey,
          targetBlock: target,
          pollMs: cqwWatchPollMs,
          useFlashbots: cqwUseFlashbots,
          useRegularTransaction: cqwUseRegularTransaction,
          skipBundleSimulation: true,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success && data.jobId) {
        cqwServerJobIdRef.current = data.jobId;
        setCqwServerJobId(data.jobId);
        setCqwWatchStatus('watching');
        if (socket) {
          socket.emit('cqw-block-watch-subscribe', { token });
        }
        return;
      }
      setError(data.error || data.details || `Could not start block watcher (HTTP ${res.status})`);
    } catch (e) {
      setError(e.message || String(e));
    }
  };

  useEffect(() => {
    if (delegationTab !== 1) {
      claimWantAutoSubmitRef.current = false;
      stopClaimExitPoll();
    }
    // CQW block watcher runs on the API server; leaving this tab does NOT stop it (use Stop watcher).
  }, [delegationTab]);

  useEffect(() => {
    if (delegationTab !== 1 || !token) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/delegation/claim-exit-autopoll/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const d = await r.json().catch(() => ({}));
        if (cancelled || !d.success) return;
        if (!d.active || !d.job?.jobId) {
          claimServerJobIdRef.current = null;
          setClaimServerJobId(null);
          return;
        }
        claimServerJobIdRef.current = d.job.jobId;
        setClaimServerJobId(d.job.jobId);
        setClaimPollStatus('polling');
        if (socket) {
          socket.emit('claim-exit-autopoll-subscribe', { token });
        }
      } catch (_) {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [delegationTab, token, socket]);

  useEffect(() => {
    if (delegationTab !== 2 || !token) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/delegation/complete-qw-block-watch/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const d = await r.json().catch(() => ({}));
        if (cancelled || !d.success) return;
        if (!d.active || !d.job?.jobId) {
          cqwServerJobIdRef.current = null;
          setCqwServerJobId(null);
          setCqwWatchStatus('idle');
          return;
        }
        cqwServerJobIdRef.current = d.job.jobId;
        setCqwServerJobId(d.job.jobId);
        setCqwWatchStatus('watching');
        if (d.job.currentBlock != null) setCqwWatchCurrentBlock(d.job.currentBlock);
        if (d.job.remainingBlocks != null) setCqwWatchRemaining(d.job.remainingBlocks);
        if (socket) {
          socket.emit('cqw-block-watch-subscribe', { token });
        }
      } catch (_) {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [delegationTab, token, socket]);

  useEffect(() => {
    if (!socket) return undefined;
    const onClaimExit = (payload) => {
      if (!payload || !payload.event) return;
      if (
        payload.jobId &&
        claimServerJobIdRef.current &&
        payload.jobId !== claimServerJobIdRef.current
      ) {
        return;
      }
      switch (payload.event) {
        case 'started':
        case 'polling':
          setClaimPollStatus('polling');
          break;
        case 'index':
          setClaimLastDisplay(payload.exitQueueIndexDisplay || '');
          setClaimMethodUsed(payload.methodUsed || '');
          if (!payload.isSentinel && payload.exitQueueIndex != null) {
            setClaimExitQueueIndex(payload.exitQueueIndex);
            setClaimPollStatus('ready');
          } else {
            setClaimExitQueueIndex(null);
            if (claimServerJobIdRef.current) setClaimPollStatus('polling');
          }
          break;
        case 'submitting':
          setClaimSubmitting(true);
          setClaimPollStatus('polling');
          if (payload.exitQueueIndex != null) setClaimExitQueueIndex(payload.exitQueueIndex);
          break;
        case 'success':
          setClaimSubmitting(false);
          setClaimPollStatus('idle');
          claimServerJobIdRef.current = null;
          setClaimServerJobId(null);
          setSuccess(
            payload.txHash
              ? `claimExitedAssets submitted (server). ${payload.method || ''} ${payload.txHash}`
              : payload.message || 'Claim submitted (server).'
          );
          void loadDataLatestRef.current();
          break;
        case 'submit_error':
          setClaimSubmitting(false);
          setError(payload.message || 'Submit failed (server autopoll)');
          break;
        case 'poll_error':
          setError(payload.message || 'Poll failed (server)');
          break;
        case 'error':
          setClaimSubmitting(false);
          setError(payload.message || 'Server autopoll error');
          break;
        case 'stopped':
          setClaimSubmitting(false);
          if (payload.reason === 'replaced_by_new_job') return;
          claimServerJobIdRef.current = null;
          setClaimServerJobId(null);
          setClaimPollStatus('idle');
          if (payload.reason === 'max_retries') {
            setError('Server autopoll stopped after max submit retries');
          }
          break;
        default:
          break;
      }
    };
    socket.on('claim-exit-autopoll', onClaimExit);
    return () => socket.off('claim-exit-autopoll', onClaimExit);
  }, [socket]);

  useEffect(() => {
    if (!socket) return undefined;
    const onCqwWatch = (payload) => {
      if (!payload || !payload.event) return;
      if (
        payload.jobId &&
        cqwServerJobIdRef.current &&
        payload.jobId !== cqwServerJobIdRef.current
      ) {
        return;
      }
      switch (payload.event) {
        case 'started':
        case 'watching':
          setCqwWatchStatus('watching');
          if (payload.currentBlock != null) setCqwWatchCurrentBlock(payload.currentBlock);
          if (payload.remainingBlocks != null) setCqwWatchRemaining(payload.remainingBlocks);
          break;
        case 'submitting':
          setCqwWatchStatus('submitting');
          setCqwSubmitting(true);
          if (payload.currentBlock != null) setCqwWatchCurrentBlock(payload.currentBlock);
          if (payload.remainingBlocks != null) setCqwWatchRemaining(payload.remainingBlocks);
          break;
        case 'success':
          setCqwWatchStatus('idle');
          setCqwSubmitting(false);
          cqwServerJobIdRef.current = null;
          setCqwServerJobId(null);
          setSuccess(
            payload.txHash
              ? `completeQueuedWithdrawals submitted (server). ${payload.method || ''} ${payload.txHash}`
              : payload.message || 'completeQueuedWithdrawals submitted (server).'
          );
          void loadDataLatestRef.current();
          break;
        case 'submit_error':
          setCqwSubmitting(false);
          setCqwWatchStatus('watching');
          setError(payload.message || 'Submit failed (server block watch)');
          break;
        case 'error':
          setCqwSubmitting(false);
          setCqwWatchStatus('error');
          setError(payload.message || 'Server block watcher error');
          break;
        case 'stopped':
          setCqwSubmitting(false);
          if (payload.reason === 'replaced_by_new_job') return;
          cqwServerJobIdRef.current = null;
          setCqwServerJobId(null);
          setCqwWatchStatus('idle');
          if (payload.reason === 'max_retries') {
            setError('Server block watcher stopped after max submit retries');
          }
          break;
        default:
          break;
      }
    };
    socket.on('cqw-block-watch', onCqwWatch);
    return () => socket.off('cqw-block-watch', onCqwWatch);
  }, [socket]);

  useEffect(() => {
    return () => {
      if (claimServerJobIdRef.current) return;
      claimPollActiveRef.current = false;
      if (claimPollTimerRef.current) {
        clearTimeout(claimPollTimerRef.current);
      }
    };
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'pending': return 'warning';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  const formatAddress = (address) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  // Helper function to check if a string is a valid Ethereum private key
  const isValidPrivateKey = (key) =>
    key === '' || (/^0x[a-fA-F0-9]{64}$/.test(key) && key.length === 66);
  
  // Helper function to derive sponsor address from private key
  const getSponsorAddress = (privateKey) => {
    if (!privateKey || privateKey === '') return null;
    try {
      const wallet = new ethers.Wallet(privateKey);
      return wallet.address;
    } catch (error) {
      return null;
    }
  };
  
  // Helper function to check if a string is a valid Ethereum address
  const isValidAddress = (address) => {
    return address === '' || ethers.isAddress(address);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={{ xs: '50vh', md: 400 }}
        sx={{ py: 6 }}
      >
        <CircularProgress size={44} thickness={4} />
      </Box>
    );
  }

  const claimPollChip =
    claimPollStatus === 'polling'
      ? {
          color: 'warning',
          icon: <HourglassEmptyIcon sx={{ fontSize: 18 }} />,
          label: claimServerJobId ? 'Server: polling exit index…' : 'Polling exit index…',
        }
      : claimPollStatus === 'ready'
        ? {
            color: 'success',
            icon: <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />,
            label: claimServerJobId ? 'Server: exit index ready' : 'Exit index ready',
          }
        : claimPollStatus === 'error'
          ? { color: 'error', icon: <ErrorOutlineIcon sx={{ fontSize: 18 }} />, label: 'Poll error' }
          : {
              color: 'default',
              icon: <RadioButtonUncheckedIcon sx={{ fontSize: 18 }} />,
              label: claimServerJobId ? 'Server job idle' : 'Idle',
            };

  const claimResolvedWallet = Array.isArray(wallets)
    ? wallets.find((x) => String(x.id) === String(claimWalletId))
    : null;

  return (
    <Box
      component="main"
      sx={{
        width: '100%',
        maxWidth: { xs: '100%', lg: 1440 },
        mx: 'auto',
        px: { xs: 2, sm: 2.5, md: 3 },
        py: { xs: 2, md: 3 },
        pb: { xs: 4, md: 5 },
      }}
    >
      {/* Header */}
      <Box
        display="flex"
        flexDirection={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        gap={{ xs: 2, sm: 0 }}
        mb={3}
      >
        <Typography variant="h4" component="h1" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
          Delegation Management
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: { xs: 'stretch', sm: 'flex-end' } }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDelegationDialog(true)}
            disabled={creatingDelegation}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Create Delegation
          </Button>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setStakingContractDialog(true)}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Add Staking Contract
          </Button>
          <Button
            variant={relayerStatus.isRunning ? "outlined" : "contained"}
            color={relayerStatus.isRunning ? "error" : "success"}
            startIcon={relayerStatus.isRunning ? <StopIcon /> : <PlayArrowIcon />}
            onClick={relayerStatus.isRunning ? handleStopMonitoring : handleStartMonitoring}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            {relayerStatus.isRunning ? 'Stop Monitoring' : 'Start Monitoring'}
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      
      {/* Loading Message Display */}
      {(creatingDelegation || revokingDelegation) && loadingMessage && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <CircularProgress size={16} />
            <Typography variant="body2">
              {loadingMessage}
            </Typography>
          </Box>
          <LinearProgress sx={{ mt: 1 }} />
        </Alert>
      )}

      <Tabs
        value={delegationTab}
        onChange={(e, v) => setDelegationTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          mb: 2,
          borderBottom: 1,
          borderColor: 'divider',
          '& .MuiTab-root': { minHeight: 48, textTransform: 'none', fontWeight: 600 },
        }}
      >
        <Tab label="Dashboard" />
        <Tab label="Claim exited assets" />
        <Tab label="Complete queued withdrawal" />
      </Tabs>

      {delegationTab === 1 && (
        <Card
          sx={{
            mb: 3,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: (theme) => theme.shadows[2],
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="h5" component="h2" fontWeight={700} gutterBottom>
                  Claim exited assets
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  One atomic <code>execute</code> batch: <code>claimExitedAssets</code> runs first, then a native transfer of <strong>8 ETH</strong> from the delegated EOA. ETH from the claim is available in the <em>same</em> transaction before the forward runs. Titan bundle = builder tip + type-4 (re-signed each retry). <strong>Auto-poll</strong> runs on the API server and submits as soon as the index is not <code>-1</code> (no extra Submit click); progress streams live over WebSocket while you keep the app open.
                </Typography>
              </Box>

              <Alert severity="success" variant="outlined" icon={false}>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  Transaction setup (this tab)
                </Typography>
                <Box component="ul" sx={{ pl: 2.25, my: 0, '& li': { mb: 1 } }}>
                  <Typography component="li" variant="body2">
                    <strong>Titan private relay:</strong> POST uses <code>privateRelay: titan</code> (bundle submission to the builder).
                  </Typography>
                  <Typography component="li" variant="body2">
                    <strong>Bundle order:</strong> tx 0 = sponsor plain ETH to <code>coinbase</code> (tip). tx 1 = EIP-7702 type-4 to the delegated EOA with batcher{' '}
                    <code>execute([claimExitedAssets, …, forward ETH])</code>.
                  </Typography>
                  <Typography component="li" variant="body2">
                    <strong>Type-4 vs regular:</strong> default is EIP-7702 type-4 for the main call; the “regular sponsor txs” switch only changes signing if you enable it.
                  </Typography>
                  <Typography component="li" variant="body2">
                    <strong>Simulation and retries:</strong> this tab sends <code>skipBundleSimulation: true</code> for <code>claimExitedAssets</code> only. The service re-signs tip + type-4 each block attempt (up to 22 blocks ahead) with a fresh sponsor nonce and auth.
                  </Typography>
                </Box>
              </Alert>

              <Alert severity="info" variant="outlined" icon={false}>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  Balance check (same batch)
                </Typography>
                <Typography variant="body2" component="div">
                  You do <strong>not</strong> need 8 ETH sitting in the wallet before submit. The claim step credits native ETH to the EOA inside this batch; the following <code>forwardETH</code> batcher call forwards <strong>8 ETH</strong> from that updated balance. If the claim does not credit at least <strong>8 ETH</strong> of native ETH in this execution, simulation will revert—then lower the fixed amount in code or confirm claim proceeds.
                </Typography>
              </Alert>

              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'action.hover' }}>
                <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.2 }}>
                  Contract (fixed)
                </Typography>
                <Typography sx={{ fontFamily: 'monospace', fontSize: '0.85rem', wordBreak: 'break-all' }}>
                  {CLAIM_EXITED_DEFAULTS.contractAddress}
                </Typography>
              </Paper>

              <Divider />

              <Box>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  1 · Wallet &amp; network
                </Typography>
                {claimResolvedWallet && claimResolvedWallet.isDelegated ? (
                  <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'action.hover' }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Claim signer (auto: <code>REACT_APP_CLAIM_WALLET_ADDRESS</code> or delegated wallet name matching /6\s*eth/i)
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {claimResolvedWallet.name || 'Wallet'}
                    </Typography>
                    <Typography sx={{ fontFamily: 'monospace', fontSize: '0.85rem', wordBreak: 'break-all' }}>
                      {claimResolvedWallet.address}
                    </Typography>
                  </Paper>
                ) : (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    No delegated claim wallet resolved. Set <code>REACT_APP_CLAIM_WALLET_ADDRESS</code> in the frontend env to your delegated EOA, or rename the correct delegated wallet so its name includes &quot;6 ETH&quot; (case-insensitive).
                  </Alert>
                )}
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      select
                      fullWidth
                      label="Override wallet (optional)"
                      value={claimWalletId}
                      onChange={(e) => setClaimWalletId(e.target.value)}
                      disabled={claimSubmitting}
                      helperText="Leave as auto-resolved unless you need a different delegated EOA"
                      SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 320 } } } }}
                    >
                      {Array.isArray(wallets) &&
                        wallets.filter((w) => w.isDelegated).map((wallet) => (
                          <MenuItem key={wallet.id} value={wallet.id}>
                            {wallet.name} ({formatAddress(wallet.address)})
                          </MenuItem>
                        ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      select
                      fullWidth
                      label="Chain"
                      value={claimChainKey}
                      onChange={(e) => setClaimChainKey(e.target.value)}
                      disabled={claimSubmitting}
                    >
                      <MenuItem value="ethereum">Ethereum</MenuItem>
                      <MenuItem value="sepolia">Sepolia</MenuItem>
                      <MenuItem value="bsc">BSC</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              <Box>
                <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} mb={1}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    2 · Exit queue index
                  </Typography>
                  <Chip icon={claimPollChip.icon} label={claimPollChip.label} color={claimPollChip.color} size="small" variant="outlined" />
                </Box>
                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary">positionTicket</Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{CLAIM_EXITED_DEFAULTS.positionTicket}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary">timestamp</Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{CLAIM_EXITED_DEFAULTS.timestamp}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary">exitQueueIndex</Typography>
                      <Typography variant="body2" fontWeight={claimExitQueueIndex != null ? 600 : 400}>
                        {claimExitQueueIndex != null ? claimExitQueueIndex : '— poll until not -1'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
                {(claimLastDisplay || claimPollStatus !== 'idle') && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Last RPC read: {claimLastDisplay || '—'}
                    {claimMethodUsed ? ` · ${claimMethodUsed}` : ''}
                  </Typography>
                )}
                {claimPollStatus === 'polling' && <LinearProgress sx={{ mb: 1 }} />}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" alignItems={{ sm: 'center' }}>
                  <TextField
                    type="number"
                    size="small"
                    label="Auto-poll interval (sec)"
                    value={claimExitPollMs / 1000}
                    onChange={(e) => {
                      const sec = parseFloat(e.target.value);
                      if (!Number.isFinite(sec)) return;
                      const ms = Math.round(sec * 1000);
                      setClaimExitPollMs(Math.min(600000, Math.max(1000, ms)));
                    }}
                    inputProps={{ min: 1, max: 600, step: 1 }}
                    disabled={claimSubmitting}
                    sx={{ width: { xs: '100%', sm: 200 } }}
                    helperText="1–600 s · default from REACT_APP_CLAIM_EXIT_POLL_MS (ms) or 8 s"
                  />
                  <Button variant="outlined" onClick={handlePollClaimExitOnce} disabled={claimSubmitting}>
                    Poll once
                  </Button>
                  <Button variant="contained" color="secondary" onClick={handleStartClaimExitMonitor} disabled={claimSubmitting}>
                    Auto-poll every {claimExitPollMs / 1000}s
                  </Button>
                  <Button
                    variant="outlined"
                    color="warning"
                    onClick={() => {
                      void stopClaimExitPollingAll();
                    }}
                  >
                    Stop polling
                  </Button>
                </Stack>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Auto-poll runs on the <strong>API server</strong> (keeps going if you close this tab) and pushes live updates over the same WebSocket as the dashboard. When <code>exitQueueIndex</code> is ready, the claim bundle is sent immediately (same payload as Submit). Interval applies after each poll round-trip.
                </Typography>
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  3 · Forward 8 ETH (after claim)
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'action.hover' }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Fixed recipient (inner <code>execute</code> native transfer)
                  </Typography>
                  <Typography sx={{ fontFamily: 'monospace', fontSize: '0.85rem', wordBreak: 'break-all' }}>
                    {CLAIM_FORWARD_RECIPIENT}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Amount: <strong>8 ETH</strong> (<code>CLAIM_FORWARD_ETH_WEI</code>), same transaction as the claim.
                  </Typography>
                </Paper>
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  4 · Titan bundle
                </Typography>
                <Stack spacing={1}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={claimUseFlashbots}
                        onChange={(e) => setClaimUseFlashbots(e.target.checked)}
                        disabled={claimSubmitting}
                        color="primary"
                      />
                    }
                    label="Submit via Titan (private bundle)"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={claimUseRegularTransaction}
                        onChange={(e) => setClaimUseRegularTransaction(e.target.checked)}
                        disabled={claimSubmitting}
                        color="default"
                      />
                    }
                    label="Use regular sponsor txs in bundle (not EIP-7702 type-4) — only if you know you need this"
                  />
                </Stack>
                <Alert severity="info" sx={{ mt: 2 }} icon={false}>
                  <Typography variant="body2">
                    This tab uses the fast path: <strong>no eth_callBundle simulation</strong> before each Titan submit (only for <code>claimExitedAssets</code>). EIP-7702 type-4 + coinbase tip, up to 22 target blocks, fresh auth each attempt. Reverts are discovered on-chain if the bundle lands wrong—use the generic Direct Unstake flow if you want simulation every time.
                  </Typography>
                </Alert>
              </Box>

              <Button
                variant="contained"
                color="success"
                size="large"
                fullWidth
                onClick={handleClaimExitedAssetsSubmit}
                disabled={claimSubmitting || claimExitQueueIndex == null || !claimWalletId}
                startIcon={claimSubmitting ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
              >
                {claimSubmitting ? 'Submitting bundle…' : 'Submit claim + 8 ETH'}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {delegationTab === 2 && (
        <Card
          sx={{
            mb: 3,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: (theme) => theme.shadows[2],
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="h5" component="h2" fontWeight={700} gutterBottom>
                  Complete queued withdrawal
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  One atomic <code>execute</code> batch: <code>completeQueuedWithdrawals</code> on EigenLayer DelegationManager, then{' '}
                  <code>forwardTokens</code> for the reward token to your fixed recipient. Same EIP-7702 type-4 + multi-builder bundles as the claim tab.
                  No exit-queue polling and no native ETH forward.
                </Typography>
              </Box>

              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'action.hover' }}>
                <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.2 }}>
                  DelegationManager (fixed)
                </Typography>
                <Typography sx={{ fontFamily: 'monospace', fontSize: '0.85rem', wordBreak: 'break-all' }}>
                  {COMPLETE_QW_DELEGATION_MANAGER}
                </Typography>
              </Paper>

              <Divider />

              <Box>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Wallet &amp; network (same as claim tab)
                </Typography>
                {claimResolvedWallet && claimResolvedWallet.isDelegated ? (
                  <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'action.hover' }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Signer: <code>REACT_APP_CLAIM_WALLET_ADDRESS</code> or delegated wallet name matching /6\s*eth/i
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {claimResolvedWallet.name || 'Wallet'}
                    </Typography>
                    <Typography sx={{ fontFamily: 'monospace', fontSize: '0.85rem', wordBreak: 'break-all' }}>
                      {claimResolvedWallet.address}
                    </Typography>
                  </Paper>
                ) : (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    No delegated wallet resolved. Use the same setup as Claim exited assets (env or &quot;6 ETH&quot; wallet name).
                  </Alert>
                )}
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      select
                      fullWidth
                      label="Wallet"
                      value={claimWalletId}
                      onChange={(e) => setClaimWalletId(e.target.value)}
                      disabled={cqwSubmitting}
                      SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 320 } } } }}
                    >
                      {Array.isArray(wallets) &&
                        wallets.filter((w) => w.isDelegated).map((wallet) => (
                          <MenuItem key={wallet.id} value={wallet.id}>
                            {wallet.name} ({formatAddress(wallet.address)})
                          </MenuItem>
                        ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      select
                      fullWidth
                      label="Chain"
                      value={claimChainKey}
                      onChange={(e) => setClaimChainKey(e.target.value)}
                      disabled={cqwSubmitting}
                    >
                      <MenuItem value="ethereum">Ethereum</MenuItem>
                      <MenuItem value="sepolia">Sepolia</MenuItem>
                      <MenuItem value="bsc">BSC</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Server block watcher (auto-submit)
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Watcher runs on the <strong>API server</strong> after you start it. This tab only shows status over the socket — switching tabs or closing the browser does <strong>not</strong> stop the job. Use <strong>Stop watcher</strong> to cancel.
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                  <TextField
                    label="Target block"
                    size="small"
                    value={cqwTargetBlock}
                    onChange={(e) => setCqwTargetBlock(e.target.value)}
                    disabled={cqwSubmitting}
                    sx={{ width: { xs: '100%', sm: 220 } }}
                  />
                  <TextField
                    label="Watch interval (ms)"
                    type="number"
                    size="small"
                    value={cqwWatchPollMs}
                    onChange={(e) => {
                      const n = parseInt(e.target.value, 10);
                      if (!Number.isFinite(n)) return;
                      setCqwWatchPollMs(Math.min(600000, Math.max(500, n)));
                    }}
                    inputProps={{ min: 500, max: 600000, step: 100 }}
                    disabled={cqwSubmitting}
                    sx={{ width: { xs: '100%', sm: 190 } }}
                  />
                  <Chip
                    size="small"
                    color={
                      cqwWatchStatus === 'watching'
                        ? 'warning'
                        : cqwWatchStatus === 'submitting'
                          ? 'info'
                          : cqwWatchStatus === 'error'
                            ? 'error'
                            : 'default'
                    }
                    label={
                      cqwWatchStatus === 'watching'
                        ? 'Watching'
                        : cqwWatchStatus === 'submitting'
                          ? 'Submitting'
                          : cqwWatchStatus === 'error'
                            ? 'Watcher error'
                            : 'Idle'
                    }
                    variant="outlined"
                  />
                </Stack>
                {(cqwWatchCurrentBlock != null || cqwWatchRemaining != null || cqwServerJobId) && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    {cqwServerJobId ? `Job: ${cqwServerJobId} · ` : ''}
                    {cqwWatchCurrentBlock != null ? `Current: ${cqwWatchCurrentBlock}` : 'Current: —'}
                    {' · '}
                    {cqwWatchRemaining != null ? `Remaining to target: ${cqwWatchRemaining}` : 'Remaining: —'}
                  </Typography>
                )}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.5 }}>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={startCqwBlockWatch}
                    disabled={cqwSubmitting || !claimWalletId}
                  >
                    Start server watcher
                  </Button>
                  <Button
                    variant="outlined"
                    color="warning"
                    onClick={() => {
                      void stopCqwBlockWatch();
                    }}
                  >
                    Stop watcher
                  </Button>
                </Stack>
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Withdrawal tuple (fixed on server)
                </Typography>
                <Typography variant="body2" color="text.secondary" component="div">
                  <Box component="ul" sx={{ pl: 2.25, my: 0 }}>
                    <li>
                      staker / withdrawer: <code>0x4241FcD2888BCfbF586200701815809D08A67af7</code>
                    </li>
                    <li>delegatedTo: <code>0x0000000000000000000000000000000000000000</code></li>
                    <li>nonce: 0 · startBlock: 24984617</li>
                    <li>
                      strategies: <code>0x57ba429517c3473B6d34CA9aCd56c0e735b94c02</code>
                    </li>
                    <li>scaledShares: 6000000000000000000</li>
                  </Box>
                </Typography>
              </Box>

              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'action.hover' }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Tokens &amp; forwardTokens
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  <code>tokens</code>: <code>[[{COMPLETE_QW_TOKEN}]]</code> · <code>receiveAsTokens</code>:{' '}
                  <code>[true]</code>
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  forwardTokens(token, recipient): {COMPLETE_QW_TOKEN} → {CLAIM_FORWARD_RECIPIENT}
                </Typography>
              </Paper>

              <Divider />

              <Box>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Bundle options
                </Typography>
                <Stack spacing={1}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={cqwUseFlashbots}
                        onChange={(e) => setCqwUseFlashbots(e.target.checked)}
                        disabled={cqwSubmitting}
                        color="primary"
                      />
                    }
                    label="Submit via private bundles (Titan + BeaverBuild + Flashbots)"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={cqwUseRegularTransaction}
                        onChange={(e) => setCqwUseRegularTransaction(e.target.checked)}
                        disabled={cqwSubmitting}
                      />
                    }
                    label="Use regular sponsor txs in bundle (not EIP-7702 type-4)"
                  />
                </Stack>
                <Alert severity="info" sx={{ mt: 2 }} icon={false}>
                  <Typography variant="body2">
                    Uses <code>skipBundleSimulation: true</code> for speed (same idea as the claim tab). No ETH forward in this batch.
                  </Typography>
                </Alert>
              </Box>

              <Button
                variant="contained"
                color="success"
                size="large"
                fullWidth
                onClick={handleCompleteQueuedWithdrawalSubmit}
                disabled={cqwSubmitting || !claimWalletId}
                startIcon={cqwSubmitting ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
              >
                {cqwSubmitting ? 'Submitting bundle…' : 'Submit completeQueuedWithdrawals + forwardTokens'}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {delegationTab === 0 && (
      <>
      {/* Relayer Status */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Relayer Status
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Box textAlign="center">
                <Typography variant="h4" color={relayerStatus.isRunning ? 'success.main' : 'error.main'}>
                  {relayerStatus.isRunning ? '🟢' : '🔴'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {relayerStatus.isRunning ? 'Running' : 'Stopped'}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box textAlign="center">
                <Typography variant="h4">{relayerStatus.monitoredWallets}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Monitored Wallets
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box textAlign="center">
                <Typography variant="h4">{(relayerStatus.providers || []).length}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Active Chains
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box textAlign="center">
                <Typography variant="h4">{Array.isArray(relayerStatus.contracts) ? relayerStatus.contracts.length : relayerStatus.contracts ? Object.keys(relayerStatus.contracts).length : 0}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Deployed Contracts
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Sponsor Private Key Management Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Sponsor Private Key Management
          </Typography>
          <TextField
            fullWidth
            label="Sponsor Private Key"
            type={showUserSponsorKey ? 'text' : 'password'}
            value={userSponsorKey}
            onChange={e => setUserSponsorKey(e.target.value)}
            InputProps={{
              endAdornment: (
                <IconButton
                  onClick={() => setShowUserSponsorKey(v => !v)}
                  edge="end"
                >
                  {showUserSponsorKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              ),
            }}
            placeholder="0x..."
            helperText="This private key will be used to sponsor transactions"
          />
          {getSponsorAddress(userSponsorKey) && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Sponsor Address: <span style={{ fontFamily: 'monospace' }}>{getSponsorAddress(userSponsorKey)}</span>
            </Typography>
          )}
          <Button
            variant="contained"
            size="small"
            onClick={handleSetUserSponsorKey}
            sx={{ mt: 1 }}
            disabled={!isValidPrivateKey(userSponsorKey)}
          >
            Save Sponsor Key
          </Button>
          {!isValidPrivateKey(userSponsorKey) && (
            <Typography color="error" variant="caption">
              Enter a valid 0x-prefixed 64-character private key or leave blank to remove.
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Recipient Address Management Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Recipient Address Management
          </Typography>
          <TextField
            fullWidth
            label="Recipient Address"
            type={showRecipientAddress ? 'text' : 'password'}
            value={recipientAddress}
            onChange={e => setRecipientAddress(e.target.value)}
            InputProps={{
              endAdornment: (
                <IconButton
                  onClick={() => setShowRecipientAddress(v => !v)}
                  edge="end"
                >
                  {showRecipientAddress ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              ),
            }}
            placeholder="0x..."
            helperText="Address to receive unstaked tokens. Leave empty to use sponsor address as fallback."
          />
          <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              onClick={handleSetRecipientAddress}
              disabled={!isValidAddress(recipientAddress)}
            >
              Save Recipient Address
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleRemoveRecipientAddress}
              color="error"
            >
              Remove
            </Button>
          </Box>
          {!isValidAddress(recipientAddress) && recipientAddress !== '' && (
            <Typography color="error" variant="caption">
              Enter a valid Ethereum address or leave blank to remove.
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Sponsor Key Base Token Balance Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Sponsor Key Base Token Balances
          </Typography>
          <Button onClick={fetchSponsorBalances} size="small" sx={{ mb: 1 }}>
            Refresh Balances
          </Button>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Chain</TableCell>
                  <TableCell>Sponsor Address</TableCell>
                  <TableCell>Balance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sponsorBalances && sponsorBalances.length > 0 ? sponsorBalances.map((row, idx) => (
                  <TableRow key={row.chain + row.sponsor}>
                    <TableCell>{row.chain}</TableCell>
                    <TableCell style={{ fontFamily: 'monospace' }}>{row.sponsor}</TableCell>
                    <TableCell style={{ color: row.balance < 0.01 ? 'red' : undefined }}>
                      {row.balance} {row.symbol}
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={3} align="center">No sponsor balances found</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Monitored Staking Contracts Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Monitored Staking Contracts ({Array.isArray(stakingContracts) ? stakingContracts.length : 0})
            </Typography>
            <Box display="flex" gap={1}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => setStakingContractDialog(true)}
                size="small"
              >
                Add Contract
              </Button>
              <IconButton onClick={loadData} size="small">
                <RefreshIcon />
              </IconButton>
            </Box>
          </Box>
          
          {Array.isArray(stakingContracts) && stakingContracts.length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Chain</TableCell>
                    <TableCell>Contract Name</TableCell>
                    <TableCell>Contract Address</TableCell>
                    <TableCell>Token Address</TableCell>
                    <TableCell>Unstake Method</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stakingContracts.map((contract) => (
                    <TableRow key={contract.id}>
                      <TableCell>
                        <Chip 
                          label={contract.chain_key} 
                          size="small" 
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {contract.contract_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">
                          {formatAddress(contract.contract_address)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">
                          {formatAddress(contract.token_address)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {contract.unstake_method}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={contract.is_active ? 'Active' : 'Inactive'} 
                          color={contract.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            if (window.confirm('Are you sure you want to remove this staking contract?')) {
                              handleRemoveStakingContract(contract.id);
                            }
                          }}
                          title="Remove Contract"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box 
              display="flex" 
              flexDirection="column" 
              alignItems="center" 
              py={4}
              sx={{ 
                border: '2px dashed', 
                borderColor: 'divider', 
                borderRadius: 1,
                backgroundColor: 'background.paper'
              }}
            >
              <AccountBalanceIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Staking Contracts
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center" mb={2}>
                Add staking contracts to monitor for automated unstaking and delegation.
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setStakingContractDialog(true)}
              >
                Add First Contract
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Direct Unstaking Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Direct Unstaking
            </Typography>
            <Box display="flex" gap={1}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => setDirectUnstakeDialog(true)}
                size="small"
              >
                Execute Unstake
              </Button>
              <IconButton onClick={loadData} size="small">
                <RefreshIcon />
              </IconButton>
            </Box>
          </Box>
          
          <Box 
            display="flex" 
            flexDirection="column" 
            alignItems="center" 
            py={4}
            sx={{ 
              border: '2px dashed', 
              borderColor: 'divider', 
              borderRadius: 1,
              backgroundColor: 'background.paper'
            }}
          >
            <AccountBalanceIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Direct Unstaking
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center" mb={2}>
              Execute unstaking operations directly without relying on balance methods or automated monitoring.
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setDirectUnstakeDialog(true)}
            >
              Execute Unstake
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Delegated Wallets */}
        <Grid item xs={12} lg={6}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Delegated Wallets ({Array.isArray(wallets) ? wallets.filter(w => w.isDelegated).length : 0})
                </Typography>
                <Box display="flex" gap={1}>
                  <Button
                    variant="outlined"
                    size="small"
                    color="warning"
                    onClick={() => {
                      const orphanedWallets = wallets.filter(w => w.isDelegated && !delegations.find(d => 
        d.delegator_address.toLowerCase() === w.address.toLowerCase() && 
        d.is_active
      ));
                      if (orphanedWallets.length > 0) {
                        alert(`Found ${orphanedWallets.length} orphaned delegation(s):\n${orphanedWallets.map(w => `${w.name} (${w.address})`).join('\n')}`);
                      } else {
                        alert('No orphaned delegations found. All delegated wallets have database records.');
                      }
                    }}
                  >
                    Check Orphaned
                  </Button>
                  <IconButton onClick={loadData} size="small">
                    <RefreshIcon />
                  </IconButton>
                </Box>
              </Box>
              
              {Array.isArray(wallets) && wallets.filter(w => w.isDelegated).map((wallet) => (
                <Accordion key={wallet.id} sx={{ mb: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box display="flex" alignItems="center" width="100%">
                      <AccountBalanceIcon sx={{ mr: 1 }} />
                      <Box flex={1}>
                        <Typography variant="subtitle1">{wallet.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatAddress(wallet.address)}
                        </Typography>
                      </Box>
                      <Chip 
                        label="Delegated" 
                        color="success" 
                        size="small" 
                        sx={{ mr: 1 }}
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <List dense>
                      <ListItem>
                        <ListItemText 
                          primary="Address" 
                          secondary={wallet.address}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="Delegated Chain" 
                          secondary={(() => {
                            // Check delegationSettings first for the specific delegated chain
                            if (wallet.delegationSettings) {
                              try {
                                const settings = typeof wallet.delegationSettings === 'string' 
                                  ? JSON.parse(wallet.delegationSettings) 
                                  : wallet.delegationSettings;
                                
                                if (settings.delegatedChain) {
                                  return settings.delegatedChain;
                                } else if (settings.chain) {
                                  return settings.chain;
                                }
                              } catch (e) {
                                // Fallback to parsing as string
                                if (wallet.delegationSettings.includes('delegatedChain')) {
                                  const match = wallet.delegationSettings.match(/"delegatedChain":"([^"]+)"/);
                                  if (match) return match[1];
                                }
                                if (wallet.delegationSettings.includes('chain')) {
                                  const match = wallet.delegationSettings.match(/"chain":"([^"]+)"/);
                                  if (match) return match[1];
                                }
                              }
                            }
                            
                            // Fallback: show all wallet chains if no delegation info found
                            let chains = wallet.chains;
                            if (Array.isArray(chains)) return chains.join(', ');
                            if (typeof chains === 'string') {
                              try {
                                const parsed = JSON.parse(chains);
                                if (Array.isArray(parsed)) return parsed.join(', ');
                              } catch {
                                // fallback: split by comma if not valid JSON
                                if (chains.includes(',')) return chains.split(',').map(s => s.trim().replace(/^"|"$/g, '')).join(', ');
                                return chains;
                              }
                            }
                            return '';
                          })()}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="Status" 
                          secondary={wallet.status}
                        />
                      </ListItem>
                      <ListItem>
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          onClick={() => {
                            if (window.confirm('Are you sure you want to revoke this delegation?')) {
                              // Find delegation by wallet address
                              const delegation = delegations.find(d => 
                                d.delegator_address.toLowerCase() === wallet.address.toLowerCase() && 
                                d.is_active
                              );
                              if (delegation) {
                                handleRevokeDelegation(delegation.id);
                              } else {
                                setError('No active delegation found for this wallet');
                              }
                            }
                          }}
                        >
                          Revoke Delegation
                        </Button>
                      </ListItem>
                      {wallet.isDelegated && !delegations.find(d => 
                        d.delegator_address.toLowerCase() === wallet.address.toLowerCase() && 
                        d.is_active
                      ) && (
                        <ListItem>
                          <Button
                            size="small"
                            color="warning"
                            variant="outlined"
                            onClick={() => {
                              // Try to determine the chain from wallet chains or use a default
                              let chainKey = 'sepolia'; // default
                              if (wallet.chains) {
                                if (Array.isArray(wallet.chains)) {
                                  chainKey = wallet.chains[0];
                                } else if (typeof wallet.chains === 'string') {
                                  try {
                                    const parsed = JSON.parse(wallet.chains);
                                    chainKey = Array.isArray(parsed) ? parsed[0] : 'sepolia';
                                  } catch {
                                    chainKey = wallet.chains.split(',')[0]?.trim() || 'sepolia';
                                  }
                                }
                              }
                              handleForceRevokeDelegation(wallet.id, chainKey);
                            }}
                          >
                            Force Revoke (Orphaned)
                          </Button>
                        </ListItem>
                      )}
                    </List>
                  </AccordionDetails>
                </Accordion>
              ))}
              
              {Array.isArray(wallets) && wallets.filter(w => w.isDelegated).length === 0 && (
                <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                  No delegated wallets found
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Active Delegations */}
        <Grid item xs={12} lg={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Active Delegations ({Array.isArray(delegations) ? delegations.filter(d => d.is_active).length : 0})
              </Typography>
              
              {Array.isArray(delegations) && delegations.filter(d => d.is_active).map((delegation) => (
                <Box key={delegation.id} sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="subtitle2">
                      {delegation.wallet_name}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Delegator: {formatAddress(delegation.delegator_address)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Delegate: {formatAddress(delegation.delegate_address)}
                  </Typography>
                  <Box display="flex" gap={1} mt={1}>
                    <Chip label={delegation.chain_key} size="small" />
                    <Chip 
                      label={`Nonce: ${delegation.nonce}`} 
                      size="small" 
                      variant="outlined"
                    />
                  </Box>
                </Box>
              ))}
              
              {Array.isArray(delegations) && delegations.filter(d => d.is_active).length === 0 && (
                <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                  No active delegations
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Actions */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6" gutterBottom>
                Recent Delegated Actions
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={loadData}
              >
                Refresh
              </Button>
            </Box>
              
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Wallet</TableCell>
                      <TableCell>Action</TableCell>
                      <TableCell>Chain</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell>Transaction</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {actions.slice(0, 10).map((action) => (
                      <TableRow key={action.id}>
                        <TableCell>{action.wallet_name}</TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            {action.action_type === 'unstake' && <SwapHorizIcon fontSize="small" />}
                            {action.action_type === 'withdraw' && <SendIcon fontSize="small" />}
                            {action.action_type === 'transfer' && <SendIcon fontSize="small" />}
                            {action.action_type}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={action.chain_key} size="small" />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={action.status} 
                            color={getStatusColor(action.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{formatDate(action.created_at)}</TableCell>
                        <TableCell>
                          {action.transaction_hash ? (
                            <Typography variant="body2" fontFamily="monospace">
                              {formatAddress(action.transaction_hash)}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Pending
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {Array.isArray(actions) && actions.length === 0 && (
                <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                  No actions found
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      </>
      )}

      {/* Create Delegation Dialog */}
      <Dialog 
        open={createDelegationDialog} 
        onClose={() => !creatingDelegation && setCreateDelegationDialog(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          Create New Delegation
          {creatingDelegation && (
            <Box display="flex" alignItems="center" gap={1} sx={{ mt: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                {loadingMessage || 'Creating on-chain delegation...'}
              </Typography>
            </Box>
          )}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Wallet"
                value={delegationForm.walletId}
                onChange={e => setDelegationForm(form => ({ ...form, walletId: e.target.value }))}
                sx={{ minWidth: '100%' }}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                        minWidth: 400
                      }
                    }
                  }
                }}
              >
                {Array.isArray(wallets) && wallets.filter(w => !w.isDelegated).map((wallet) => (
                  <MenuItem key={wallet.id} value={wallet.id}>
                    {wallet.name} ({formatAddress(wallet.address)})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Delegation will be created with the batcher contract as the delegate
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Chain"
                value={delegationForm.chainKey}
                onChange={e => setDelegationForm(form => ({ ...form, chainKey: e.target.value }))}
                helperText="Note: Only one delegation per wallet per chain is allowed"
                sx={{ minWidth: '100%' }}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                        minWidth: 300
                      }
                    }
                  }
                }}
              >
                <MenuItem value="ethereum">Ethereum</MenuItem>
                <MenuItem value="sepolia">Sepolia</MenuItem>
                <MenuItem value="bsc">BSC</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDelegationDialog(false)} disabled={creatingDelegation}>Cancel</Button>
          <Button 
            onClick={handleCreateDelegation} 
            variant="contained" 
            disabled={creatingDelegation}
            startIcon={creatingDelegation ? <CircularProgress size={20} /> : null}
          >
            {creatingDelegation ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sponsor Dialog */}
      <Dialog open={sponsorDialog} onClose={() => setSponsorDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Set Sponsor Private Key</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Wallet"
                value={sponsorForm.walletId}
                onChange={(e) => setSponsorForm({ ...sponsorForm, walletId: e.target.value })}
              >
                {Array.isArray(wallets) && wallets.filter(w => w.isDelegated).map((wallet) => (
                  <option key={wallet.id} value={wallet.id}>
                    {wallet.name} ({formatAddress(wallet.address)})
                  </option>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Sponsor Private Key"
                type={showPrivateKey ? 'text' : 'password'}
                value={sponsorForm.sponsorPrivateKey}
                onChange={(e) => setSponsorForm({ ...sponsorForm, sponsorPrivateKey: e.target.value })}
                placeholder="0x..."
                InputProps={{
                  endAdornment: (
                    <IconButton
                      onClick={() => setShowPrivateKey(!showPrivateKey)}
                      edge="end"
                    >
                      {showPrivateKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  ),
                }}
                helperText="This private key will be used to sponsor transactions"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSponsorDialog(false)}>Cancel</Button>
          <Button onClick={handleSetSponsor} variant="contained">Set Sponsor</Button>
        </DialogActions>
      </Dialog>

      {/* Staking Contract Dialog */}
      <Dialog open={stakingContractDialog} onClose={() => {
        setStakingContractDialog(false);
        setContractInfo(null);
      }} maxWidth="md" fullWidth>
        <DialogTitle>Add Staking Contract</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Chain"
                value={stakingContractForm.chainKey}
                onChange={e => setStakingContractForm(form => ({
                  ...form,
                  chainKey: e.target.value
                }))}
                sx={{ minWidth: '100%' }}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                        minWidth: 300
                      }
                    }
                  }
                }}
              >
                <MenuItem value="ethereum">Ethereum</MenuItem>
                <MenuItem value="sepolia">Sepolia</MenuItem>
                <MenuItem value="bsc">BSC</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <Box display="flex" gap={2} alignItems="flex-end">
                <TextField
                  fullWidth
                  label="Contract Address"
                  value={stakingContractForm.contractAddress}
                  onChange={(e) => {
                    setStakingContractForm({ ...stakingContractForm, contractAddress: e.target.value });
                    setContractInfo(null); // Clear previous info when address changes
                  }}
                  placeholder="0x..."
                />
                <Button
                  variant="outlined"
                  onClick={handleCheckContractInfo}
                  disabled={checkingContract || !stakingContractForm.contractAddress || !stakingContractForm.chainKey}
                  sx={{ minWidth: 120, height: 56 }}
                >
                  {checkingContract ? <CircularProgress size={20} /> : 'Check Contract'}
                </Button>
              </Box>
              
              {/* Contract Info Display */}
              {contractInfo && (
                <Box mt={2} p={2} bgcolor="background.paper" border={1} borderColor="divider" borderRadius={1}>
                  <Typography variant="subtitle2" gutterBottom>
                    Contract Information
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Code Length: {contractInfo.codeLength}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Chain: {contractInfo.chainKey}
                      </Typography>
                    </Grid>
                    {contractInfo.isProxy ? (
                      <>
                        <Grid item xs={12}>
                          <Alert severity="info" sx={{ mt: 1 }}>
                            <Typography variant="body2">
                              🔄 <strong>Proxy Contract Detected!</strong>
                            </Typography>
                            <Typography variant="body2">
                              Type: {contractInfo.proxyType}
                            </Typography>
                            <Typography variant="body2" fontFamily="monospace" fontSize="0.8rem">
                              Implementation: {contractInfo.implementationAddress}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Implementation Code Length: {contractInfo.implementationCodeLength}
                            </Typography>
                          </Alert>
                        </Grid>
                      </>
                    ) : (
                      <Grid item xs={12}>
                        <Alert severity="success" sx={{ mt: 1 }}>
                          <Typography variant="body2">
                            📋 Direct Contract (Not a proxy)
                          </Typography>
                        </Alert>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Contract Name"
                value={stakingContractForm.contractName}
                onChange={(e) => setStakingContractForm({ ...stakingContractForm, contractName: e.target.value })}
                placeholder="e.g., Uniswap V3 Staking"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Token Address"
                value={stakingContractForm.tokenAddress}
                onChange={(e) => setStakingContractForm({ ...stakingContractForm, tokenAddress: e.target.value })}
                placeholder="0x..."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reward Token Address (Optional)"
                value={stakingContractForm.rewardTokenAddress}
                onChange={(e) => setStakingContractForm({ ...stakingContractForm, rewardTokenAddress: e.target.value })}
                placeholder="0x..."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Unstake Method"
                value={stakingContractForm.unstakeMethod || ''}
                onChange={e => {
                  const method = e.target.value;
                  const found = commonUnstakeMethods.find(m => m.value === method);
                  // Extract the actual method name (before the underscore)
                  const actualMethod = method.includes('_') ? method.split('_')[0] : method;
                  setStakingContractForm(form => ({
                    ...form,
                    unstakeMethod: method,
                    unstakeParams: found ? found.params : [],
                    unstakeParamTypes: found ? found.paramTypes : [],
                    customUnstakeMethod: method === 'custom' ? '' : actualMethod
                  }));
                }}
                sx={{ minWidth: '100%' }}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                        minWidth: 400
                      }
                    }
                  }
                }}
              >
                {commonUnstakeMethods.map(method => (
                  <MenuItem key={method.value} value={method.value}>{method.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            {stakingContractForm.unstakeMethod === 'custom' && (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Custom Unstake Method"
                    value={stakingContractForm.customUnstakeMethod}
                    onChange={e => setStakingContractForm(form => ({ ...form, customUnstakeMethod: e.target.value }))}
                    placeholder="e.g., claimRewards"
                  />
                </Grid>
                {/* Dynamic parameter fields for custom method */}
                {stakingContractForm.unstakeParams && stakingContractForm.unstakeParams.map((param, idx) => (
                  <Box key={`custom-param-${idx}`} display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                    <TextField
                      select
                      label={`Param ${idx + 1} Type`}
                      value={param}
                      onChange={e => {
                        const newParams = [...stakingContractForm.unstakeParams];
                        newParams[idx] = e.target.value;
                        setStakingContractForm(form => ({ ...form, unstakeParams: newParams }));
                      }}
                      sx={{ minWidth: 150, flexGrow: 1 }}
                    >
                      <MenuItem value="">No Parameter</MenuItem>
                      <MenuItem value="uint256">uint256</MenuItem>
                      <MenuItem value="address">address</MenuItem>
                      <MenuItem value="bool">bool</MenuItem>
                      <MenuItem value="bytes">bytes</MenuItem>
                      <MenuItem value="string">string</MenuItem>
                      <MenuItem value="uint128">uint128</MenuItem>
                      <MenuItem value="uint64">uint64</MenuItem>
                      <MenuItem value="uint32">uint32</MenuItem>
                    </TextField>
                    <IconButton onClick={() => {
                      const newParams = stakingContractForm.unstakeParams.filter((_, i) => i !== idx);
                      setStakingContractForm(form => ({ ...form, unstakeParams: newParams }));
                    }}>
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setStakingContractForm(form => ({
                    ...form,
                    unstakeParams: [...(form.unstakeParams || []), 'uint256']
                  }))}
                  sx={{ mt: 1 }}
                >
                  Add Parameter
                </Button>
              </>
            )}
            {stakingContractForm.unstakeMethod !== 'custom' && stakingContractForm.unstakeParams.length > 0 && (
              <>
                {/* Parameter fields for common methods with params */}
                {stakingContractForm.unstakeParams.map((param, idx) => (
                  <Box key={`common-param-${idx}`} display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                    <TextField
                      select
                      label={`Param ${idx + 1} Type`}
                      value={param}
                      onChange={e => {
                        const newParams = [...stakingContractForm.unstakeParams];
                        newParams[idx] = e.target.value;
                        setStakingContractForm(form => ({ ...form, unstakeParams: newParams }));
                      }}
                      sx={{ minWidth: 150, flexGrow: 1 }}
                    >
                      <MenuItem value="">No Parameter</MenuItem>
                      <MenuItem value="uint256">uint256</MenuItem>
                      <MenuItem value="address">address</MenuItem>
                      <MenuItem value="bool">bool</MenuItem>
                      <MenuItem value="bytes">bytes</MenuItem>
                      <MenuItem value="string">string</MenuItem>
                      <MenuItem value="uint128">uint128</MenuItem>
                      <MenuItem value="uint64">uint64</MenuItem>
                      <MenuItem value="uint32">uint32</MenuItem>
                    </TextField>
                  </Box>
                ))}
              </>
            )}
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Balance Method"
                value={stakingContractForm.balanceMethod || ''}
                onChange={e => {
                  const method = e.target.value;
                  const found = commonBalanceMethods.find(m => m.value === method);
                  setStakingContractForm(form => ({
                    ...form,
                    balanceMethod: method,
                    balanceReturnType: found ? found.returnType : form.balanceReturnType,
                    customBalanceMethod: method === 'custom' ? '' : method
                  }));
                }}
                sx={{ minWidth: '100%' }}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                        minWidth: 400
                      }
                    }
                  }
                }}
              >
                {commonBalanceMethods.map(method => (
                  <MenuItem key={method.value} value={method.value}>{method.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            {stakingContractForm.balanceMethod === 'custom' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Custom Balance Method"
                  value={stakingContractForm.customBalanceMethod}
                  onChange={e => setStakingContractForm(form => ({ ...form, customBalanceMethod: e.target.value }))}
                  placeholder="e.g., getUserStakes"
                />
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Balance Return Type"
                value={stakingContractForm.balanceReturnType || ''}
                onChange={e => {
                  const returnType = e.target.value;
                  setStakingContractForm(form => ({
                    ...form,
                    balanceReturnType: returnType,
                    customBalanceReturnType: returnType === 'custom' ? '' : returnType
                  }));
                }}
                sx={{ minWidth: '100%' }}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                        minWidth: 400
                      }
                    }
                  }
                }}
              >
                {commonBalanceReturnTypes.map(type => (
                  <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            {stakingContractForm.balanceReturnType === 'custom' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Custom Balance Return Type"
                  value={stakingContractForm.customBalanceReturnType}
                  onChange={e => setStakingContractForm(form => ({ ...form, customBalanceReturnType: e.target.value }))}
                  placeholder="e.g., uint256"
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setStakingContractDialog(false);
            setContractInfo(null);
          }}>Cancel</Button>
          <Button onClick={handleAddStakingContract} variant="contained">Add Contract</Button>
        </DialogActions>
      </Dialog>

      {/* Direct Unstaking Dialog */}
      <Dialog open={directUnstakeDialog} onClose={() => {
        if (!directUnstaking) {
          setDirectUnstakeDialog(false);
          setDirectUnstakeContractInfo(null);
        }
      }} maxWidth="md" fullWidth>
        <DialogTitle>
          Execute Direct Unstaking
          {directUnstaking && (
            <Box display="flex" alignItems="center" gap={1} sx={{ mt: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                {loadingMessage || 'Executing unstaking transaction...'}
              </Typography>
            </Box>
          )}
        </DialogTitle>
        <DialogContent>
          {directUnstaking && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
              <Typography variant="body2" color="info.contrastText">
                <strong>Status:</strong> {loadingMessage || 'Executing unstaking transaction...'}
              </Typography>
            </Box>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Wallet"
                value={directUnstakeForm.walletId}
                onChange={e => setDirectUnstakeForm(form => ({ ...form, walletId: e.target.value }))}
                disabled={directUnstaking}
                sx={{ minWidth: '100%' }}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                        minWidth: 400
                      }
                    }
                  }
                }}
              >
                {Array.isArray(wallets) && wallets.filter(w => w.isDelegated).map((wallet) => (
                  <MenuItem key={wallet.id} value={wallet.id}>
                    {wallet.name} ({formatAddress(wallet.address)})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Chain"
                value={directUnstakeForm.chainKey}
                onChange={e => setDirectUnstakeForm(form => ({ ...form, chainKey: e.target.value }))}
                disabled={directUnstaking}
                sx={{ minWidth: '100%' }}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                        minWidth: 300
                      }
                    }
                  }
                }}
              >
                <MenuItem value="ethereum">Ethereum</MenuItem>
                <MenuItem value="sepolia">Sepolia</MenuItem>
                <MenuItem value="bsc">BSC</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <Box display="flex" gap={2} alignItems="flex-end">
                <TextField
                  fullWidth
                  label="Contract Address"
                  value={directUnstakeForm.contractAddress}
                  onChange={(e) => {
                    setDirectUnstakeForm({ ...directUnstakeForm, contractAddress: e.target.value });
                    setDirectUnstakeContractInfo(null); // Clear previous info when address changes
                  }}
                  placeholder="0x..."
                  disabled={directUnstaking}
                />
                <Button
                  variant="outlined"
                  onClick={handleCheckDirectUnstakeContractInfo}
                  disabled={checkingDirectUnstakeContract || !directUnstakeForm.contractAddress || !directUnstakeForm.chainKey || directUnstaking}
                  sx={{ minWidth: 120, height: 56 }}
                >
                  {checkingDirectUnstakeContract ? <CircularProgress size={20} /> : 'Check Contract'}
                </Button>
              </Box>
              
              {/* Direct Unstake Contract Info Display */}
              {directUnstakeContractInfo && (
                <Box mt={2} p={2} bgcolor="background.paper" border={1} borderColor="divider" borderRadius={1}>
                  <Typography variant="subtitle2" gutterBottom>
                    Contract Information
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Code Length: {directUnstakeContractInfo.codeLength}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Chain: {directUnstakeContractInfo.chainKey}
                      </Typography>
                    </Grid>
                    {directUnstakeContractInfo.isProxy ? (
                      <>
                        <Grid item xs={12}>
                          <Alert severity="info" sx={{ mt: 1 }}>
                            <Typography variant="body2">
                              🔄 <strong>Proxy Contract Detected!</strong>
                            </Typography>
                            <Typography variant="body2">
                              Type: {directUnstakeContractInfo.proxyType}
                            </Typography>
                            <Typography variant="body2" fontFamily="monospace" fontSize="0.8rem">
                              Implementation: {directUnstakeContractInfo.implementationAddress}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Implementation Code Length: {directUnstakeContractInfo.implementationCodeLength}
                            </Typography>
                          </Alert>
                        </Grid>
                      </>
                    ) : (
                      <Grid item xs={12}>
                        <Alert severity="success" sx={{ mt: 1 }}>
                          <Typography variant="body2">
                            📋 Direct Contract (Not a proxy)
                          </Typography>
                        </Alert>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Unstake Method"
                value={directUnstakeForm.unstakeMethod || ''}
                disabled={directUnstaking}
                onChange={e => {
                  const method = e.target.value;
                  const found = commonUnstakeMethods.find(m => m.value === method);
                  const actualMethod = method.includes('_') ? method.split('_')[0] : method;
                  setDirectUnstakeForm(form => ({
                    ...form,
                    unstakeMethod: method,
                    unstakeParams: found ? found.params : [],
                    unstakeParamTypes: found ? found.paramTypes : [],
                    customUnstakeMethod: method === 'custom' ? '' : actualMethod
                  }));
                }}
                sx={{ minWidth: '100%' }}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                        minWidth: 400
                      }
                    }
                  }
                }}
              >
                {commonUnstakeMethods.map(method => (
                  <MenuItem key={method.value} value={method.value}>{method.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            {directUnstakeForm.unstakeMethod === 'custom' && (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Custom Unstake Method"
                    value={directUnstakeForm.customUnstakeMethod}
                    onChange={e => setDirectUnstakeForm(form => ({ ...form, customUnstakeMethod: e.target.value }))}
                    placeholder="e.g., claimRewards"
                    disabled={directUnstaking}
                  />
                </Grid>
                {/* Dynamic parameter fields for custom method */}
                {directUnstakeForm.unstakeParams && directUnstakeForm.unstakeParams.map((param, idx) => (
                  <Box key={`custom-param-${idx}`} display="flex" flexDirection="column" gap={1} sx={{ mb: 2 }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <TextField
                        select
                        label={`Param ${idx + 1} Type`}
                        value={param}
                        onChange={e => {
                          const newParams = [...directUnstakeForm.unstakeParams];
                          newParams[idx] = e.target.value;
                          setDirectUnstakeForm(form => ({ ...form, unstakeParams: newParams }));
                        }}
                        sx={{ minWidth: 150, flexGrow: 1 }}
                      >
                        <MenuItem value="">No Parameter</MenuItem>
                        <MenuItem value="uint256">uint256</MenuItem>
                        <MenuItem value="address">address</MenuItem>
                        <MenuItem value="bool">bool</MenuItem>
                        <MenuItem value="bytes">bytes</MenuItem>
                        <MenuItem value="string">string</MenuItem>
                        <MenuItem value="uint128">uint128</MenuItem>
                        <MenuItem value="uint64">uint64</MenuItem>
                        <MenuItem value="uint32">uint32</MenuItem>
                        <MenuItem value="address[]">address[]</MenuItem>
                        <MenuItem value="uint256[]">uint256[]</MenuItem>
                        <MenuItem value="bytes32[]">bytes32[]</MenuItem>
                        <MenuItem value="string[]">string[]</MenuItem>
                      </TextField>
                      <IconButton onClick={() => {
                        const newParams = directUnstakeForm.unstakeParams.filter((_, i) => i !== idx);
                        const newParamTypes = directUnstakeForm.unstakeParamTypes.filter((_, i) => i !== idx);
                        setDirectUnstakeForm(form => ({ 
                          ...form, 
                          unstakeParams: newParams,
                          unstakeParamTypes: newParamTypes
                        }));
                      }}>
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                    <TextField
                      fullWidth
                      label={`Parameter ${idx + 1} Value`}
                      value={directUnstakeForm.unstakeParamTypes[idx] || ''}
                      onChange={e => {
                        const newParamTypes = [...directUnstakeForm.unstakeParamTypes];
                        newParamTypes[idx] = e.target.value;
                        setDirectUnstakeForm(form => ({ ...form, unstakeParamTypes: newParamTypes }));
                      }}
                      placeholder={`Enter ${param} value`}
                      helperText={`Parameter type: ${param}`}
                      disabled={directUnstaking}
                    />
                  </Box>
                ))}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setDirectUnstakeForm(form => ({
                    ...form,
                    unstakeParams: [...(form.unstakeParams || []), 'uint256'],
                    unstakeParamTypes: [...(form.unstakeParamTypes || []), '']
                  }))}
                  disabled={directUnstaking}
                  sx={{ mt: 1 }}
                >
                  Add Parameter
                </Button>
              </>
            )}
            
            {/* Parameter value fields for non-custom methods */}
            {directUnstakeForm.unstakeMethod !== 'custom' && directUnstakeForm.unstakeParams && directUnstakeForm.unstakeParams.map((param, idx) => (
              <Grid item xs={12} key={`unstake-param-${idx}`}>
                <TextField
                  fullWidth
                  label={`Parameter ${idx + 1} Value`}
                  value={directUnstakeForm.unstakeParamTypes[idx] || ''}
                  onChange={e => {
                    const newParamTypes = [...directUnstakeForm.unstakeParamTypes];
                    newParamTypes[idx] = e.target.value;
                    setDirectUnstakeForm(form => ({ ...form, unstakeParamTypes: newParamTypes }));
                  }}
                  placeholder={`Enter ${param} value`}
                  helperText={`Parameter type: ${param}${directUnstakeForm.unstakeParamTypes[idx] ? ` (${directUnstakeForm.unstakeParamTypes[idx]})` : ''}`}
                  disabled={directUnstaking}
                />
              </Grid>
            ))}
            
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Post-Unstake Transfers (Optional, Multiple Supported)
              </Typography>
              {(directUnstakeForm.transferInstructions || []).map((transfer, idx) => (
                <Box key={idx} sx={{ mb: 2, p: 2, border: '1px solid #2c2c2c', borderRadius: 1 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label={`Unstake Amount #${idx + 1} (Optional)`}
                        value={transfer.unstakeAmount}
                        onChange={(e) => {
                          const updated = [...(directUnstakeForm.transferInstructions || [])];
                          updated[idx] = { ...updated[idx], unstakeAmount: e.target.value };
                          setDirectUnstakeForm({ ...directUnstakeForm, transferInstructions: updated });
                        }}
                        placeholder="e.g., 1000000000000000000 (in wei)"
                        disabled={directUnstaking}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label={`Token Address #${idx + 1}`}
                        value={transfer.tokenAddress}
                        onChange={(e) => {
                          const updated = [...(directUnstakeForm.transferInstructions || [])];
                          updated[idx] = { ...updated[idx], tokenAddress: e.target.value };
                          setDirectUnstakeForm({ ...directUnstakeForm, transferInstructions: updated });
                        }}
                        placeholder="0x..."
                        disabled={directUnstaking}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label={`Recipient Address #${idx + 1}`}
                        value={transfer.recipientAddress}
                        onChange={(e) => {
                          const updated = [...(directUnstakeForm.transferInstructions || [])];
                          updated[idx] = { ...updated[idx], recipientAddress: e.target.value };
                          setDirectUnstakeForm({ ...directUnstakeForm, transferInstructions: updated });
                        }}
                        placeholder="0x..."
                        disabled={directUnstaking}
                      />
                    </Grid>
                  </Grid>
                  {(directUnstakeForm.transferInstructions || []).length > 1 && (
                    <Box sx={{ mt: 1 }}>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => {
                          const updated = (directUnstakeForm.transferInstructions || []).filter((_, i) => i !== idx);
                          setDirectUnstakeForm({
                            ...directUnstakeForm,
                            transferInstructions: updated.length ? updated : [{ unstakeAmount: '', tokenAddress: '', recipientAddress: '' }]
                          });
                        }}
                        disabled={directUnstaking}
                      >
                        Remove Transfer
                      </Button>
                    </Box>
                  )}
                </Box>
              ))}
              <Button
                size="small"
                onClick={() => {
                  setDirectUnstakeForm({
                    ...directUnstakeForm,
                    transferInstructions: [
                      ...(directUnstakeForm.transferInstructions || []),
                      { unstakeAmount: '', tokenAddress: '', recipientAddress: '' }
                    ]
                  });
                }}
                disabled={directUnstaking}
              >
                Add Another Token Transfer
              </Button>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Forward native ETH after unstake (optional)
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Uses batcher <code>forwardETH(recipient, amount)</code> after token transfers, in the same <code>execute</code> batch. Leave amount empty to use the wallet&apos;s current ETH balance when you submit (use an explicit amount if unstake credits native ETH in the same transaction).
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="ETH recipient address"
                    value={directUnstakeForm.forwardEthRecipient}
                    onChange={(e) =>
                      setDirectUnstakeForm({ ...directUnstakeForm, forwardEthRecipient: e.target.value })
                    }
                    placeholder="0x..."
                    disabled={directUnstaking}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Amount (optional)"
                    value={directUnstakeForm.forwardEthAmount}
                    onChange={(e) =>
                      setDirectUnstakeForm({ ...directUnstakeForm, forwardEthAmount: e.target.value })
                    }
                    placeholder="wei integer, or e.g. 0.05 for ETH"
                    disabled={directUnstaking}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" alignItems="center" gap={1}>
                <input
                  type="checkbox"
                  checked={directUnstakeForm.useFlashbots}
                  onChange={(e) => setDirectUnstakeForm({ ...directUnstakeForm, useFlashbots: e.target.checked, privateRelay: 'titan' })}
                  disabled={directUnstaking}
                />
                <Typography variant="body2">
                  Use Titan Private Relay (MEV Protection) - Provides private transaction submission and MEV protection
                </Typography>
              </Box>
              {directUnstakeForm.useFlashbots && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    🚀 Titan private relay enabled: Your transaction will be submitted privately with MEV protection
                  </Typography>
                </Alert>
              )}
              {directUnstakeForm.useFlashbots && directUnstakeForm.chainKey !== 'ethereum' && directUnstakeForm.chainKey !== 'sepolia' && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    ⚠️ Private relays are only supported on Ethereum and Sepolia networks
                  </Typography>
                </Alert>
              )}
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" alignItems="center" gap={1}>
                <input
                  type="checkbox"
                  checked={directUnstakeForm.useRegularTransaction}
                  onChange={(e) => setDirectUnstakeForm({ ...directUnstakeForm, useRegularTransaction: e.target.checked })}
                  disabled={directUnstaking}
                />
                <Typography variant="body2">
                  Use Regular Transaction - Provides regular transaction submission
                </Typography>
              </Box>
              {directUnstakeForm.useRegularTransaction && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    🚀 Regular transaction enabled: Your transaction will be submitted regularly
                  </Typography>
                </Alert>
              )}
              {directUnstakeForm.useRegularTransaction && directUnstakeForm.chainKey !== 'ethereum' && directUnstakeForm.chainKey !== 'sepolia' && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    ⚠️ Regular transaction is only supported on Ethereum and Sepolia networks
                  </Typography>
                </Alert>
              )}
            </Grid>

          </Grid>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setDirectUnstakeDialog(false);
              setDirectUnstakeContractInfo(null);
            }}
            disabled={directUnstaking}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDirectUnstake} 
            variant="contained"
            disabled={directUnstaking}
            startIcon={directUnstaking ? <CircularProgress size={16} /> : null}
          >
            {directUnstaking ? 'Executing...' : 'Execute Unstake'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Delegation;